import {useState, useRef, useEffect, useMemo, useCallback} from "react";
import {useQuery, useMutation} from "@tanstack/react-query";
import {useRoute, useLocation, useSearch} from "wouter";
import {queryClient, apiRequest} from "@/lib/queryClient";
import { bumpSpaceInCache } from "@/lib/sidebarRecencyCache";
import {
    type MastermindSpace,
    type SpaceRelationship,
    type SpaceAsset,
    type Tag,
    type Topic,
    type ChatThread,
    type Source,
    type SubjectMatterExpert
} from "@shared/schema";
import {mastermindSpacesService} from "@/services/mastermindSpacesService";
import {chatThreadsService} from "@/services/chatThreadsService";
import {ragService} from "@/services/ragService";
import {sourcesService} from "@/services/sourcesService";
import {tagsService} from "@/services/tagsService";
import {knowledgeBaseService, isKnowledgeBaseDocLinkedToSpace, type KnowledgeBaseItem} from "@/services/knowledgeBaseService";
import {supabase} from "@/lib/supabase";
import {useAuth} from "@/contexts/AuthContext";
import {useWorkspace} from "@/contexts/WorkspaceContext";
import {useOnboarding} from "@/contexts/OnboardingContext";
import {useMastermind} from "@/contexts/MastermindContext";
import {spaceSharingService} from "@/services/spaceSharingService";
import type { SpaceMemberRole } from "@/services/sharedSpacePermissionService";
import {topicsService} from "@/services/topicsService";
import {LockedEntityOverlay} from "@/components/EntityLockButton";
import {PinVerificationDialog} from "@/components/PinVerificationDialog";
import {Button} from "@/components/ui/button";
import {Card, CardContent, CardHeader, CardTitle} from "@/components/ui/card";
import {Tabs, TabsContent, TabsList, TabsTrigger} from "@/components/ui/tabs";
import {IconSearch, IconSelectMenu} from "@/components/CompactToolbar";
import {Badge} from "@/components/ui/badge";
import {Input} from "@/components/ui/input";
import {Textarea} from "@/components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter
} from "@/components/ui/dialog";
import {Label} from "@/components/ui/label";
import {ScrollArea} from "@/components/ui/scroll-area";
import {Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger} from "@/components/ui/sheet";
import {Collapsible, CollapsibleContent, CollapsibleTrigger} from "@/components/ui/collapsible";
import {useToast} from "@/hooks/use-toast";
import {
    ArrowLeft, Upload, Search, Network, FileText, MessageSquare, Settings,
    Plus, FolderOpen, MessageCircle, Target, CheckCircle2, Circle,
    ChevronDown, ChevronRight, Bot, Zap, Pencil, Trash2, MoreVertical, Brain,
    BookOpen, Link2, X, ExternalLink, Globe, FileIcon, Focus, Save, Eye, User, Menu,
    Sparkles, Tag as TagIcon, Share2, Users, StickyNote, LayoutGrid, List, ArrowUp, ArrowDown,
    Lock, LockOpen
} from "lucide-react";
import {type SpaceLens} from "@shared/schema";
import {TagPicker} from "@/components/TagPicker";
import {TopicsManagement} from "@/components/TopicsManagement";
import {DocumentGrid} from "@/components/DocumentGrid";
import {SectionErrorBoundary} from "@/components/SectionErrorBoundary";
import {RichTextEditor} from "@/components/RichTextEditor";
// import {RelatedContent} from "@/components/RelatedContent";
import {SpaceInsights} from "@/components/SpaceInsights";
import {SmartTagSuggestions} from "@/components/SmartTagSuggestions";
import {GoogleDrivePicker} from "@/components/GoogleDrivePicker";
import {ShareSpaceDialog} from "@/components/ShareSpaceDialog";
import {ThreadItem} from "@/components/ThreadItem";
import {ThreadLinkingDialog} from "@/components/ThreadLinkingDialog"; // Added this import
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select";
import {Switch} from "@/components/ui/switch";
import {useViewPreferences, type SortDirection} from "@/hooks/use-view-preferences";
import {formatDistanceToNow} from "date-fns";
import {useKnowledgeBaseList} from "@/contexts/KnowledgeBaseContext.tsx";
import {useSpaceMemberKnowledgeDocuments} from "@/hooks/useSharedSpaceKnowledgeDocuments";
import { partitionMerged, isThreadMerged, isSourceMerged } from "@/lib/spaceMergeUi";
import { SpaceMergedSection, MergedRowChrome } from "@/components/space-merge";

const THREAD_TITLE_MAX_LENGTH = 22;

function truncateTitle(title: string | undefined | null, maxLength: number): string {
    if (!title) return "";
    return title.length <= maxLength ? title : title.slice(0, maxLength) + "...";
}

// Safe date parsing helper to prevent "Invalid time value" errors on mobile
function safeGetTime(dateValue: string | number | Date | null | undefined): number {
    if (!dateValue) return 0;
    try {
        const date = new Date(dateValue);
        const time = date.getTime();
        // Check if date is valid (getTime returns NaN for invalid dates)
        return isNaN(time) ? 0 : time;
    } catch {
        return 0;
    }
}

function formatSpaceRoleLabel(role: SpaceMemberRole): string {
    return role.charAt(0).toUpperCase() + role.slice(1);
}

function spaceRoleBadgeVariant(role: SpaceMemberRole): "default" | "secondary" | "outline" {
    if (role === "owner") return "default";
    if (role === "editor") return "secondary";
    return "outline";
}

export default function MastermindSpaceDetail() {
    const [, params] = useRoute("/spaces/:id");
    const [, setLocation] = useLocation();
    const searchString = useSearch();
    const spaceId = params?.id || "";
    const {toast} = useToast();
    const {user} = useAuth();
    const {currentWorkspaceId} = useWorkspace();
    const {completeMilestone, hasMilestone} = useOnboarding();
    const {setCurrentSpaceId, setCurrentTopicId} = useMastermind();

    const [activeTab, setActiveTab] = useState("topics");
    const [selectedTopicIdForLink, setSelectedTopicIdForLink] = useState<string | null>(null);
    const centerColumnRef = useRef<HTMLDivElement>(null);
    const [centerContentWidthPx, setCenterContentWidthPx] = useState<number | null>(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
    const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
    const [uploadedImageUrl, setUploadedImageUrl] = useState("");
    const [isUploading, setIsUploading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState("");
    const [isEditingSpace, setIsEditingSpace] = useState(false);
    const [editName, setEditName] = useState("");
    const [editDescription, setEditDescription] = useState("");
    const [isAddingGoal, setIsAddingGoal] = useState(false);
    const [newGoal, setNewGoal] = useState("");
    const [isEditingLens, setIsEditingLens] = useState(false);
    const [editLens, setEditLens] = useState<SpaceLens>({purpose: "", priorities: "", details: ""});
    const fileInputRef = useRef<HTMLInputElement>(null);
    const sourceUploadRef = useRef<HTMLInputElement>(null);

    // Add Source Dialog state
    const [isAddSourceDialogOpen, setIsAddSourceDialogOpen] = useState(false);
    const [sourceType, setSourceType] = useState<"text" | "url" | "upload" | "google-doc">("text");
    const [newSourceTitle, setNewSourceTitle] = useState("");
    const [newSourceContent, setNewSourceContent] = useState("");
    const [newSourceUrl, setNewSourceUrl] = useState("");
    const [newSourceTags, setNewSourceTags] = useState<string[]>([]);
    const [newSourceTopics, setNewSourceTopics] = useState<string[]>([]);
    const [sourceUploadFiles, setSourceUploadFiles] = useState<{ id: string; filename: string; url: string }[]>([]);
    const [isGoogleDrivePickerOpen, setIsGoogleDrivePickerOpen] = useState(false);

    // Subject Matter Experts state
    const [isAddExpertDialogOpen, setIsAddExpertDialogOpen] = useState(false);
    const [isRenameThreadDialogOpen, setIsRenameThreadDialogOpen] = useState(false);
    const [threadToRename, setThreadToRename] = useState<{id: string; title: string} | null>(null);
    const [newThreadTitle, setNewThreadTitle] = useState("");
    const [threadToLink, setThreadToLink] = useState<{id: string; linkedSpaceIds: string[]; topicId: string | null; tags: string[]} | null>(null);
    const [newExpertName, setNewExpertName] = useState("");
    const [newExpertRole, setNewExpertRole] = useState("");
    const [newExpertNotes, setNewExpertNotes] = useState("");
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    // Delete Space Dialog state
    const [isDeleteSpaceDialogOpen, setIsDeleteSpaceDialogOpen] = useState(false);
    const [deleteSpaceConfirmText, setDeleteSpaceConfirmText] = useState("");

    // Track if locked space has been unlocked for this session (persist in sessionStorage)
    const UNLOCK_KEY = (id: string) => `space-unlocked-${id}`;
    const [isSpaceUnlocked, setIsSpaceUnlocked] = useState(false);
    const [showSpacePinDialog, setShowSpacePinDialog] = useState(false);

    // Restore unlocked state from sessionStorage when spaceId is available
    useEffect(() => {
      if (!spaceId || typeof window === "undefined") return;
      try {
        if (sessionStorage.getItem(UNLOCK_KEY(spaceId)) === "1") {
          setIsSpaceUnlocked(true);
        }
      } catch {
        // ignore
      }
    }, [spaceId]);
    const [membersOpen, setMembersOpen] = useState(false);

    // Notes state for auto-save
    const [notesContent, setNotesContent] = useState("");
    const [isNotesSaving, setIsNotesSaving] = useState(false);
    const notesTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Collapsible section states with localStorage persistence
    const getStoredState = (key: string, defaultValue: boolean = false): boolean => {
        if (typeof window === "undefined") return defaultValue;
        const stored = localStorage.getItem(`space-ribbon-${key}`);
        return stored !== null ? stored === "true" : defaultValue;
    };

    const [quickActionsOpen, setQuickActionsOpen] = useState(() => getStoredState("quickActions"));
    const [aiAgentsOpen, setAiAgentsOpen] = useState(() => getStoredState("aiAgents"));
    const [recentThreadsOpen, setRecentThreadsOpen] = useState(() => getStoredState("recentThreads"));
    const [aiBriefingOpen, setAiBriefingOpen] = useState(() => getStoredState("aiBriefing"));
    const [goalsOpen, setGoalsOpen] = useState(() => getStoredState("goals"));
    const [milestonesOpen, setMilestonesOpen] = useState(() => getStoredState("milestones"));
    const [tagsOpen, setTagsOpen] = useState(() => getStoredState("tags"));
    const [linkedSpacesOpen, setLinkedSpacesOpen] = useState(() => getStoredState("linkedSpaces"));
    const [linkedSourcesOpen, setLinkedSourcesOpen] = useState(() => getStoredState("linkedSources"));

    // Sidebar panel collapse state - default to collapsed for simpler UI
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => getStoredState("sidebarCollapsed", true));

    const toggleSidebarCollapsed = () => {
        const newValue = !isSidebarCollapsed;
        setIsSidebarCollapsed(newValue);
        if (typeof window !== "undefined") {
            localStorage.setItem(`space-ribbon-sidebarCollapsed`, String(newValue));
        }
    };

    // Persist collapsible state to localStorage
    const toggleSection = (key: string, value: boolean, setter: (v: boolean) => void) => {
        setter(value);
        if (typeof window !== "undefined") {
            localStorage.setItem(`space-ribbon-${key}`, String(value));
        }
    };

    // Fetch space data (scoped by current workspace or personal)
    const {data: space, isLoading} = useQuery<MastermindSpace>({
        queryKey: ["/api/spaces", spaceId, currentWorkspaceId ?? "personal"],
        queryFn: async () => {
            if (!spaceId) return null;
            const result = await mastermindSpacesService.getMastermindSpace(spaceId, currentWorkspaceId ?? null);
            if (!result) throw new Error("Space not found");
            return result;
        },
        enabled: !!spaceId,
    });

    // Check if current user is the space owner (PIN lock only applies to owner)
    const {data: isSpaceOwner = false} = useQuery<boolean>({
        queryKey: ["/api/spaces", spaceId, "is-owner"],
        queryFn: async () => {
            if (!spaceId) return false;
            return await spaceSharingService.isSpaceOwner(spaceId);
        },
        enabled: !!spaceId && !!space,
        staleTime: 60000, // Cache for 1 minute
    });

    // When a space is opened, touch it immediately so it becomes "most recent" in the sidebar list
    const lastTouchedSpaceIdRef = useRef<string | null>(null);
    useEffect(() => {
        if (!spaceId || !user?.id) return;
        if (lastTouchedSpaceIdRef.current === spaceId) return;
        lastTouchedSpaceIdRef.current = spaceId;

        bumpSpaceInCache(spaceId);

        // Touch the space immediately (don't wait for space data to load)
        mastermindSpacesService.touchSpace(spaceId).then(() => {
            // Invalidate all space-related queries to update the sidebar instantly
            queryClient.invalidateQueries({ queryKey: ["spaces", user.id] });
            queryClient.invalidateQueries({ queryKey: ["/api/spaces"] });
            queryClient.invalidateQueries({ queryKey: ["/api/spaces", user.id] });
            queryClient.invalidateQueries({ queryKey: ["/api/mastermind-spaces"] });
        }).catch((error) => {
            console.warn("Failed to touch space:", error);
        });
    }, [spaceId, user?.id, queryClient]);

    // Set global space/topic context so right-panel document open/create auto-links to this space (and selected topic)
    useEffect(() => {
        if (spaceId) {
            setCurrentSpaceId(spaceId);
        }
        return () => {
            setCurrentSpaceId(null);
            setCurrentTopicId(null);
        };
    }, [spaceId, setCurrentSpaceId, setCurrentTopicId]);

    useEffect(() => {
        setCurrentTopicId(selectedTopicIdForLink);
    }, [selectedTopicIdForLink, setCurrentTopicId]);

    // Fetch space relationships
    const {data: relationships = []} = useQuery<SpaceRelationship[]>({
        queryKey: ["/api/spaces", spaceId, "relationships"],
        queryFn: async () => {
            if (!spaceId) return [];
            const {data, error} = await supabase
                .from("space_relationships")
                .select("id, source_space_id, target_space_id, relationship_type, rationale, strength, created_at")
                .or(`source_space_id.eq.${spaceId},target_space_id.eq.${spaceId}`);

            if (error) {
                console.error("Error fetching relationships:", error);
                throw new Error(`Failed to fetch relationships: ${error.message}`);
            }

            return (data || []).map((item: any) => ({
                id: item.id,
                sourceSpaceId: item.source_space_id,
                targetSpaceId: item.target_space_id,
                relationshipType: item.relationship_type,
                rationale: item.rationale,
                strength: item.strength,
                createdAt: item.created_at,
            })) as SpaceRelationship[];
        },
        enabled: !!spaceId,
    });

    // Fetch linked space data
    const linkedSpaceIds = relationships.map(rel =>
        rel.targetSpaceId === spaceId ? rel.sourceSpaceId : rel.targetSpaceId
    );

    const {data: linkedSpaces = {}} = useQuery<Record<string, MastermindSpace>>({
        queryKey: ["/api/spaces", "linked", currentWorkspaceId ?? "personal", ...linkedSpaceIds.sort()],
        queryFn: async () => {
            if (linkedSpaceIds.length === 0) return {};
            const spaces = await Promise.all(
                linkedSpaceIds.map(async id => {
                    try {
                        const space = await mastermindSpacesService.getMastermindSpace(id, currentWorkspaceId ?? null);
                        return space ? {id, data: space} : null;
                    } catch {
                        return null;
                    }
                })
            );
            return spaces.reduce((acc, item) => {
                if (item) acc[item.id] = item.data;
                return acc;
            }, {} as Record<string, MastermindSpace>);
        },
        enabled: linkedSpaceIds.length > 0,
    });

    // Fetch space assets
    const {data: assets = []} = useQuery<SpaceAsset[]>({
        queryKey: ["/api/spaces", spaceId, "assets"],
        queryFn: async () => {
            if (!spaceId) return [];
            const {data, error} = await supabase
                .from("space_assets")
                .select("id, space_id, asset_type, url, metadata, is_primary, created_at")
                .eq("space_id", spaceId)
                .order("created_at", {ascending: false});

            if (error) {
                console.error("Error fetching assets:", error);
                throw new Error(`Failed to fetch assets: ${error.message}`);
            }

            return (data || []).map((item: any) => ({
                id: item.id,
                spaceId: item.space_id,
                assetType: item.asset_type,
                url: item.url,
                metadata: item.metadata || {},
                isPrimary: item.is_primary || "false",
                createdAt: item.created_at,
            })) as SpaceAsset[];
        },
        enabled: !!spaceId,
    });

    // Fetch topics for this space (works for both owned and shared spaces)
    const {data: topics = [], isLoading: spaceDataBeingLoaded } = useQuery<Topic[]>({
        queryKey: ["/api/spaces", spaceId, "topics"],
        queryFn: async () => {
            if (!spaceId) return [];

            // Check if user has access to this space
            const { spaceSharingService } = await import("@/services/spaceSharingService");
            const hasAccess = await spaceSharingService.hasSpaceAccess(spaceId);
            const ownerId = await spaceSharingService.getSpaceOwnerUserId(spaceId);
            const myRole = await spaceSharingService.getCurrentUserSpaceRole(spaceId);

            if (!hasAccess) {
                return [];
            }

            const fetched = await topicsService.getTopics(spaceId);
            return fetched;
        },
        enabled: !!spaceId,
    });

    // Thread summary type for lightweight listing (no messages)
    type ThreadSummary = Omit<ChatThread, 'messages'> & { messageCount: number };

    // Fetch space members (needed for shared spaces to fetch threads/documents from all members)
    const {data: spaceMembers = [], isLoading: isLoadingMembers} = useQuery({
        queryKey: ["/api/spaces", spaceId, "members"],
        queryFn: async () => {
            if (!spaceId) return [];
            return await spaceSharingService.getSpaceMembers(spaceId);
        },
        enabled: !!spaceId && !!space,
    });

    const {data: spaceOwnerUserId = null} = useQuery({
        queryKey: ["/api/spaces", spaceId, "owner-id"],
        queryFn: () => (spaceId ? spaceSharingService.getSpaceOwnerUserId(spaceId) : Promise.resolve(null)),
        enabled: !!spaceId,
    });

    const {data: mySpaceRole = null} = useQuery({
        queryKey: ["/api/spaces", spaceId, "my-role", user?.id],
        queryFn: () => (spaceId ? spaceSharingService.getCurrentUserSpaceRole(spaceId) : Promise.resolve(null)),
        enabled: !!spaceId && !!user?.id,
    });

    const {data: myItemOverrides = {}} = useQuery({
        queryKey: ["/api/spaces", spaceId, "item-overrides", user?.id],
        queryFn: () => (spaceId ? spaceSharingService.getMyItemOverridesForSpace(spaceId) : Promise.resolve({})),
        enabled: !!spaceId && !!user?.id && !!(space as { isShared?: boolean } | null)?.isShared,
    });

    // Get member user IDs for fetching threads and documents (for shared spaces)
    const memberUserIds = useMemo(() => {
        if (!spaceMembers || spaceMembers.length === 0) {
            // If no members yet, return current user ID (fallback)
            return user?.id ? [user.id] : [];
        }
        return spaceMembers.map(m => m.userId);
    }, [spaceMembers, user?.id]);

    // Fetch current user's own threads for linking (scoped by current workspace or personal)
    const {data: userThreads = [], isLoading: isLoadingUserThreads} = useQuery<ThreadSummary[]>({
        queryKey: ['/api/chat-thread-summaries', user?.id, currentWorkspaceId ?? 'personal'],
        queryFn: async () => {
            if (!user) return [];
            return await chatThreadsService.getThreadSummaries(currentWorkspaceId ?? null);
        },
        enabled: !!user,
        staleTime: 30000,
    });

    // Fetch threads from other space members (for shared spaces only)
    const otherMemberUserIds = useMemo(() => {
        if (!user?.id || !spaceMembers || spaceMembers.length === 0) return [];
        return spaceMembers
            .map(m => m.userId)
            .filter(id => id !== user.id); // Exclude current user
    }, [spaceMembers, user?.id]);

    const {data: memberThreads = [], isLoading: isLoadingMemberThreads} = useQuery<ThreadSummary[]>({
        queryKey: ['/api/chat-thread-summaries-members', spaceId, otherMemberUserIds.sort().join(",")],
        queryFn: async () => {
            if (otherMemberUserIds.length === 0) return [];
            // Fetch threads from other members only
            return await chatThreadsService.getThreadSummariesForUsers(otherMemberUserIds);
        },
        enabled: otherMemberUserIds.length > 0,
        staleTime: 30000,
    });

    // Combine user's own threads with member threads (for display)
    const allThreads = useMemo(() => {
        const combined = [...userThreads, ...memberThreads];
        // Deduplicate by thread ID
        const seen = new Set<string>();
        return combined.filter(thread => {
            if (seen.has(thread.id)) return false;
            seen.add(thread.id);
            return true;
        });
    }, [userThreads, memberThreads]);

    const isLoadingThreads = isLoadingUserThreads || isLoadingMemberThreads;

    // Helper function to get topicIds from thread (supports both array and single value for backward compatibility)
    const getThreadTopicIds = (thread: ThreadSummary): string[] => {
        // Check for topicIds array first (new format)
        if (Array.isArray((thread as any).topicIds)) {
            return (thread as any).topicIds.filter((id: any) => id != null).map((id: any) => String(id));
        }
        
        // Fallback to single topicId (backward compatibility)
        if ((thread as any).topicId) {
            return [String((thread as any).topicId)];
        }
        
        return [];
    };

    // Helper function to get spaceIds from thread (supports both array and single value for backward compatibility)
    const getThreadSpaceIds = (thread: ThreadSummary): string[] => {
        const linkedSpaceIds = (thread as any).linkedSpaceIds;
        
        // Check for linkedSpaceIds array first (new format - already jsonb)
        if (Array.isArray(linkedSpaceIds)) {
            return linkedSpaceIds.filter((id: any) => id != null).map((id: any) => String(id));
        }
        
        // Fallback to single value if it exists
        if (linkedSpaceIds) {
            return [String(linkedSpaceIds)];
        }
        
        return [];
    };

    // Filter linked threads from allThreads client-side
    // Include threads that are:
    // 1. Directly linked to this space (via linkedSpaceIds array)
    // 2. OR linked to any topic in this space (via topicIds array or topicId single value)
    const linkedThreads = useMemo(() => {
        return allThreads.filter((thread) => {
            // Check if thread is directly linked to this space
            const threadSpaceIds = getThreadSpaceIds(thread);
            const isDirectlyLinked = threadSpaceIds.includes(spaceId);
            
            // Check if thread is linked to any topic in this space
            const threadTopicIds = getThreadTopicIds(thread);
            const isLinkedToSpaceTopic = topics.some(topic => {
                return threadTopicIds.some(tid => String(tid) === String(topic.id));
            });
            
            return isDirectlyLinked || isLinkedToSpaceTopic;
        });
    }, [allThreads, spaceId, topics]);
    // console.log(`-------------------------linkedThreads-`);
    // console.log(linkedThreads);

    const { native: nativeLinkedThreads, merged: mergedLinkedThreads } = useMemo(
        () => partitionMerged(linkedThreads, isThreadMerged),
        [linkedThreads]
    );

    // Keep recentThreads for backward compatibility (used in sidebar)
    const recentThreads = linkedThreads.slice(0, 10);

    // Thread search and view preferences
    const [threadSearchQuery, setThreadSearchQuery] = useState("");
    type ThreadSortField = "title" | "createdAt" | "updatedAt" | "lineage";
    const {
        viewMode: threadViewMode,
        setViewMode: setThreadViewMode,
        sortField: threadSortField,
        setSortField: setThreadSortField,
        sortDirection: threadSortDirection,
        setSortDirection: setThreadSortDirection,
    } = useViewPreferences<ThreadSortField>({
        storageKey: `threads.${spaceId}`,
        defaultViewMode: "list",
        defaultSortField: "updatedAt",
        defaultSortDirection: "desc",
    });

    // Create a map of topicId to topic name for displaying topic names in threads
    const topicNameMap = useMemo(() => {
        const map: Record<string, string> = {};
        topics.forEach(topic => {
            map[topic.id] = topic.name;
        });
        return map;
    }, [topics]);

    // Per-thread lineage (how thread is linked to this space) and group (topic or Unlinked)
    const threadLineageMap = useMemo(() => {
        const map = new Map<string, { lineageLabel: string; groupKey: string; groupLabel: string }>();
        for (const thread of linkedThreads) {
            const threadTopicIds = getThreadTopicIds(thread);
            const topicIdsInThisSpace = threadTopicIds.filter(tid =>
                topics.some(t => String(t.id) === String(tid))
            );
            if (topicIdsInThisSpace.length === 0) {
                map.set(thread.id, {
                    lineageLabel: "Space",
                    groupKey: "UNLINKED",
                    groupLabel: "Unlinked",
                });
            } else {
                const names = topicIdsInThisSpace.map(id => topicNameMap[id] || id).join(", ");
                const firstTopicId = topicIdsInThisSpace[0];
                map.set(thread.id, {
                    lineageLabel: `Space → ${names}`,
                    groupKey: String(firstTopicId),
                    groupLabel: topicNameMap[firstTopicId] || firstTopicId,
                });
            }
        }
        return map;
    }, [linkedThreads, topics, topicNameMap]);

    // Group order: each topic in space order, then Unlinked
    const threadGroupOrder = useMemo(() => {
        const order: string[] = topics.map(t => String(t.id));
        order.push("UNLINKED");
        return order;
    }, [topics]);

    // Threads grouped by topic or Unlinked (for display)
    const threadGroups = useMemo(() => {
        const filtered = nativeLinkedThreads.filter((thread) => {
            if (!threadSearchQuery) return true;
            const query = threadSearchQuery.toLowerCase();
            return thread.title?.toLowerCase().includes(query) ?? false;
        });

        // Sort: when lineage, sort by group then by selected field; otherwise current sort
        filtered.sort((a, b) => {
            if (threadSortField === "lineage") {
                const aMeta = threadLineageMap.get(a.id);
                const bMeta = threadLineageMap.get(b.id);
                const aOrder = aMeta ? threadGroupOrder.indexOf(aMeta.groupKey) : threadGroupOrder.length;
                const bOrder = bMeta ? threadGroupOrder.indexOf(bMeta.groupKey) : threadGroupOrder.length;
                if (aOrder !== bOrder) return threadSortDirection === "asc" ? aOrder - bOrder : bOrder - aOrder;
                // Within group, fall back to title
                return threadSortDirection === "asc"
                    ? (a.title || "").localeCompare(b.title || "")
                    : (b.title || "").localeCompare(a.title || "");
            }
            let comparison = 0;
            switch (threadSortField) {
                case "title":
                    comparison = (a.title || "").localeCompare(b.title || "");
                    break;
                case "createdAt":
                    comparison = safeGetTime(a.createdAt) - safeGetTime(b.createdAt);
                    break;
                case "updatedAt":
                    comparison = safeGetTime(a.updatedAt) - safeGetTime(b.updatedAt);
                    break;
                default:
                    comparison = safeGetTime(a.updatedAt) - safeGetTime(b.updatedAt);
            }
            return threadSortDirection === "asc" ? comparison : -comparison;
        });

        // Bucket into groups preserving order
        const groups: { groupKey: string; groupLabel: string; threads: ThreadSummary[] }[] = [];
        const seenKeys = new Set<string>();
        for (const key of threadGroupOrder) {
            const threadsInGroup = filtered.filter(t => threadLineageMap.get(t.id)?.groupKey === key);
            if (threadsInGroup.length > 0) {
                groups.push({
                    groupKey: key,
                    groupLabel: key === "UNLINKED" ? "Unlinked" : (topicNameMap[key] || key),
                    threads: threadsInGroup,
                });
                seenKeys.add(key);
            }
        }
        return groups;
    }, [nativeLinkedThreads, threadSearchQuery, threadSortField, threadSortDirection, threadLineageMap, threadGroupOrder, topicNameMap]);

    const mergedThreadsFilteredSorted = useMemo(() => {
        const filtered = mergedLinkedThreads.filter((thread) => {
            if (!threadSearchQuery) return true;
            const query = threadSearchQuery.toLowerCase();
            return thread.title?.toLowerCase().includes(query) ?? false;
        });
        const arr = [...filtered];
        arr.sort((a, b) => {
            if (threadSortField === "lineage") {
                const c = safeGetTime(a.updatedAt) - safeGetTime(b.updatedAt);
                return threadSortDirection === "asc" ? c : -c;
            }
            let comparison = 0;
            switch (threadSortField) {
                case "title":
                    comparison = (a.title || "").localeCompare(b.title || "");
                    break;
                case "createdAt":
                    comparison = safeGetTime(a.createdAt) - safeGetTime(b.createdAt);
                    break;
                case "updatedAt":
                    comparison = safeGetTime(a.updatedAt) - safeGetTime(b.updatedAt);
                    break;
                default:
                    comparison = safeGetTime(a.updatedAt) - safeGetTime(b.updatedAt);
            }
            return threadSortDirection === "asc" ? comparison : -comparison;
        });
        return arr;
    }, [mergedLinkedThreads, threadSearchQuery, threadSortField, threadSortDirection]);

    // Thread embedding status for RAG (Embed / Re-embed in Threads tab menu)
    const linkedThreadIds = useMemo(() => linkedThreads.map((t) => t.id), [linkedThreads]);
    const { data: threadEmbeddingStatusMap = {} } = useQuery<Record<string, boolean>>({
        queryKey: ["thread-embedding-status-space", spaceId, linkedThreadIds.slice().sort().join(",")],
        queryFn: () => ragService.getThreadEmbeddingStatusForThreads(linkedThreadIds),
        enabled: linkedThreadIds.length > 0,
        staleTime: 30000,
    });

    // Fetch AI agents
    const {data: agents = []} = useQuery<any[]>({
        queryKey: ["/api/ai-agents"],
        queryFn: async () => {
            const { aiAgentsService } = await import("@/services/aiAgentsService");
            return aiAgentsService.getAgents();
        },
    });

    // Fetch all spaces for linking (current workspace or personal)
    const {data: allSpaces = []} = useQuery<MastermindSpace[]>({
        queryKey: ["/api/spaces", currentWorkspaceId ?? "personal"],
        queryFn: async () => {
            return await mastermindSpacesService.getMastermindSpaces(currentWorkspaceId ?? null);
        },
    });

    // Fetch sources linked to this space
    const {data: linkedSources = []} = useQuery<Source[]>({
        queryKey: ["/api/sources", spaceId],
        queryFn: async () => {
            if (!spaceId) return [];
            return await sourcesService.getSources(spaceId);
        },
        enabled: !!spaceId,
    });

    const { native: nativeLinkedSources, merged: mergedLinkedSources } = useMemo(
        () => partitionMerged(linkedSources, isSourceMerged),
        [linkedSources]
    );

    // Fetch all tags
    const {data: allTags = []} = useQuery<Tag[]>({
        queryKey: ["/api/tags"],
        queryFn: async () => {
            return await tagsService.getTags();
        },
    });

    // Fetch current user's own documents using useKnowledgeBaseList (for real-time updates)
    const topicIds = useMemo(() => topics.map(topic => topic.id), [topics]);
    
    const {
        items: userDocuments,
        isLoading: isLoadingUserDocuments,
        refresh: refreshUserDocuments,
    } = useKnowledgeBaseList({ enabled: true });

    // Wrapper function to refresh both user documents and member documents
    const refreshKnowledge = useCallback(() => {
        // Refresh user's documents (from useKnowledgeBaseList - has real-time updates)
        refreshUserDocuments();
        // Invalidate member documents query
        queryClient.invalidateQueries({ queryKey: ["/api/knowledgebase-members", spaceId] });
        // Also invalidate general knowledgebase queries for compatibility
        queryClient.invalidateQueries({ queryKey: ["/api/knowledgebase", spaceId] });
    }, [refreshUserDocuments, queryClient, spaceId]);

    // Filter user's documents to only those relevant to this space
    const userDocumentsForSpace = useMemo(() => {
        if (!spaceId) return [];
        console.log('userDocumentsbeforefilter', userDocuments);
        return userDocuments.filter(doc => {
            if (isKnowledgeBaseDocLinkedToSpace(doc, spaceId)) return true;

            // Include if linked to any topic in the space
            if (topicIds.length > 0) {
                // Check new format: topicIds array (from metadata)
                const metadata = doc.metadata || {};
                const parsedMetadata = typeof metadata === 'string'
                    ? (() => { try { return JSON.parse(metadata); } catch { return {}; } })()
                    : metadata;

                const docTopicIds = Array.isArray(parsedMetadata.topicIds)
                    ? parsedMetadata.topicIds
                    : (Array.isArray(doc.topicIds) ? doc.topicIds : []);

                if (docTopicIds.length > 0) {
                    const docTopicIdsStr = docTopicIds.map(id => String(id));
                    if (topicIds.some(topicId => docTopicIdsStr.includes(String(topicId)))) {
                        return true;
                    }
                }

                // Check old format: topicId single value
                const docTopicId = parsedMetadata.topicId || doc.topicId;
                if (docTopicId) {
                    const docTopicIdStr = typeof docTopicId === 'string' ? docTopicId : String(docTopicId);
                    if (topicIds.some(topicId => {
                        const normalizedTopicId = typeof topicId === 'string' ? topicId : String(topicId);
                        return docTopicIdStr === normalizedTopicId ||
                            Number(docTopicIdStr) === Number(normalizedTopicId);
                    })) {
                        return true;
                    }
                }
            }

            return false;
        });
    }, [userDocuments, spaceId, topicIds]);

    const {
        memberDocuments,
        isLoading: isLoadingMemberDocuments,
    } = useSpaceMemberKnowledgeDocuments(spaceId, {
        enabled: !!spaceId && !!space,
    });

    // Combine user's own documents with member documents (for display)
    // Note: For linking/attaching, components should filter to only user's documents (userDocumentsForSpace)
    const allDocuments = useMemo(() => {
        const combined = [...userDocumentsForSpace, ...memberDocuments];
        // Deduplicate by document ID
        const seen = new Set<string>();
        return combined.filter(doc => {
            if (seen.has(doc.id)) return false;
            seen.add(doc.id);
            return true;
        });
    }, [userDocumentsForSpace, memberDocuments]);

    const isLoadingDocuments = isLoadingUserDocuments || isLoadingMemberDocuments;

    const isOwner = user?.id && spaceMembers.find(m => m.userId === user.id && m.spaceRole === "owner");

    const {data: memberUsers = {}} = useQuery<Record<string, {email: string; username: string; name?: string}>>({
        queryKey: ["/api/spaces", spaceId, "member-profiles", spaceMembers.map(m => m.userId).join(",")],
        queryFn: async () => {
            const users: Record<string, {email: string; username: string; name?: string}> = {};
            for (const member of spaceMembers) {
                // Avoid querying a "users" table whose id is bigint (auth uses UUID). Use a stable display label.
                const shortId = member.userId.slice(0, 8);
                users[member.userId] = {
                    username: `Member (${shortId})`,
                    email: "",
                    name: undefined,
                };
            }
            return users;
        },
        enabled: spaceMembers.length > 0,
    });


    const updateMemberRoleMutation = useMutation({
        mutationFn: async ({ memberUserId, role }: { memberUserId: string; role: "editor" | "viewer" }) => {
            if (!spaceId) throw new Error("Space ID is required");
            return await spaceSharingService.updateMemberSpaceRole(spaceId, memberUserId, role);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["/api/spaces", spaceId, "members"] });
            queryClient.invalidateQueries({ queryKey: ["/api/spaces", spaceId, "my-role", user?.id] });
            queryClient.invalidateQueries({ queryKey: ["spaces"] });
            queryClient.invalidateQueries({ queryKey: ["/api/spaces"] });
            toast({ title: "Role updated" });
        },
        onError: (error: any) => {
            toast({
                title: "Failed to update role",
                description: error?.message || "Could not update member role",
                variant: "destructive",
            });
        },
    });

    const removeMemberMutation = useMutation({
        mutationFn: async (accessId: string) => {
            if (!spaceId) throw new Error("Space ID is required");
            return await spaceSharingService.removeMember(spaceId, accessId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["/api/spaces", spaceId, "members"]});
            queryClient.invalidateQueries({ queryKey: ["/api/spaces", spaceId, "my-role", user?.id] });
            queryClient.invalidateQueries({ queryKey: ["spaces"] });
            queryClient.invalidateQueries({ queryKey: ["/api/spaces"] });
            toast({title: "Member Removed", description: "Member has been removed from the space"});
        },
        onError: (error: any) => {
            toast({
                title: "Failed to Remove Member",
                description: error.message || "Could not remove member",
                variant: "destructive",
            });
        },
    });

    // Get spaces that are not already linked
    const availableSpacesToLink = allSpaces.filter(s =>
        s.id !== spaceId && !linkedSpaceIds.includes(s.id)
    );

    // Get sources that are not linked to this space
    // Note: Sources are now derived from knowledge base items via useKnowledgeBaseList hook
    // Filter knowledge base items that could be used as sources (those not already linked to this space)
    const availableSourcesToLink = useMemo(() => {
        // For now, return empty array since sources are separate from knowledge base items
        // If sources need to be available for linking, they should be fetched separately
        // or knowledge base items with isSource: true could be used
        return [];
    }, [spaceId, userDocuments]);

    // Get threads that are not linked to this space
    // IMPORTANT: Only show current user's own threads for linking (not other members' threads)
    const availableThreadsToLink = userThreads.filter(thread => {
        if (!spaceId) return true;
        const threadLinkedSpaceIds = thread.linkedSpaceIds || [];
        return !threadLinkedSpaceIds.includes(spaceId);
    });

    // Get primary hero image
    const heroImage = assets.find(asset => asset.assetType === "hero_image" && asset.isPrimary === "true");

    // Initialize editLens from space data when it loads
    useEffect(() => {
        if (space?.lens) {
            const lens = space.lens as SpaceLens;
            setEditLens({
                purpose: lens.purpose || "",
                priorities: lens.priorities || "",
                details: lens.details || "",
            });
        }
    }, [space]);

    // Initialize notes from space data when it loads
    useEffect(() => {
        if (space) {
            // Check if notes field exists in the space data
            // If space was loaded but 'notes' property is not present (undefined), column may not exist
            if ('notes' in space) {
                setNotesContent(space.notes || "");
                setNotesColumnExists(true);
            } else {
                // Column likely doesn't exist in database
                setNotesContent("");
                // Don't set notesColumnExists to false here - let the first save attempt detect it
                // This handles case where column exists but is null
            }
        }
    }, [space]);

    // Auto-save notes with debounce
    const [notesColumnExists, setNotesColumnExists] = useState(true); // Assume column exists until proven otherwise
    const saveNotes = async (content: string) => {
        if (!spaceId || !notesColumnExists) return;
        setIsNotesSaving(true);
        try {
            await mastermindSpacesService.updateMastermindSpace(spaceId, { notes: content });
            queryClient.invalidateQueries({ queryKey: ["/api/spaces", spaceId] });
        } catch (error: any) {
            console.error("Failed to save notes:", error);
            // Check if the error is due to missing column (42703 = undefined_column)
            const errorMessage = error?.message || "";
            if (errorMessage.includes("column") && errorMessage.includes("does not exist") ||
                errorMessage.includes("42703") || errorMessage.includes("notes")) {
                // Column doesn't exist in database - silently disable notes saving
                setNotesColumnExists(false);
                console.warn("Notes column not found in database. Please add the 'notes' TEXT column to the mastermind table.");
            } else {
                toast({ title: "Failed to save notes", variant: "destructive" });
            }
        } finally {
            setIsNotesSaving(false);
        }
    };

    const handleNotesChange = (content: string) => {
        setNotesContent(content);
        // Debounce save
        if (notesTimeoutRef.current) {
            clearTimeout(notesTimeoutRef.current);
        }
        notesTimeoutRef.current = setTimeout(() => {
            saveNotes(content);
        }, 1000); // Save after 1 second of no typing
    };

    // Cleanup notes timeout on unmount
    useEffect(() => {
        return () => {
            if (notesTimeoutRef.current) {
                clearTimeout(notesTimeoutRef.current);
            }
        };
    }, []);

    // Auto-relock space when user leaves: clear session unlock so next visit requires PIN again.
    // (Space remains locked in DB; we only clear the in-session unlock.)
    useEffect(() => {
        return () => {
            if (spaceId && typeof window !== "undefined") {
                try {
                    sessionStorage.removeItem(UNLOCK_KEY(spaceId));
                } catch {
                    // ignore
                }
            }
        };
    }, [spaceId]);

    // URL search: ?tab=topics&expandTopic=ID — open Topics tab and expand that topic (e.g. when returning from chat)
    const urlTab = useMemo(() => {
        if (!searchString) return null;
        const params = new URLSearchParams(searchString);
        return params.get("tab");
    }, [searchString]);
    const expandTopicIdFromUrl = useMemo(() => {
        if (!searchString) return null;
        const params = new URLSearchParams(searchString);
        const id = params.get("expandTopic");
        return id ? decodeURIComponent(id) : null;
    }, [searchString]);
    useEffect(() => {
        if (urlTab === "topics") setActiveTab("topics");
    }, [urlTab]);

    // ResizeObserver for Topics tab: measure center column width so topics list uses exact available space
    useEffect(() => {
        const el = centerColumnRef.current;
        if (!el) return;
        const measure = () => {
            const w = el.getBoundingClientRect().width;
            if (w > 0) setCenterContentWidthPx(w);
        };
        measure();
        requestAnimationFrame(measure);
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    // Update space mutation
    const updateSpaceMutation = useMutation({
        mutationFn: async (updates: Partial<MastermindSpace>) => {
            if (!spaceId) throw new Error("Space ID is required");
            const result = await mastermindSpacesService.updateMastermindSpace(spaceId, updates, currentWorkspaceId ?? null);
            if (!result) throw new Error("Space not found");
            return result;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["/api/spaces", spaceId]});
            queryClient.invalidateQueries({queryKey: ["/api/spaces"]});
            toast({title: "Space Updated", description: "Your changes have been saved"});
            setIsEditingSpace(false);
        },
        onError: () => {
            toast({title: "Update Failed", description: "Could not save changes", variant: "destructive"});
        },
    });

    const turnLockOffMutation = useMutation({
        mutationFn: async () => {
            if (!spaceId) throw new Error("Space ID is required");
            const result = await mastermindSpacesService.lockSpace(spaceId, false, currentWorkspaceId ?? null);
            if (!result) throw new Error("Space not found");
            return result;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["/api/spaces", spaceId] });
            queryClient.invalidateQueries({ queryKey: ["/api/spaces"] });
            if (typeof window !== "undefined") {
                try { sessionStorage.removeItem(UNLOCK_KEY(spaceId)); } catch { /* ignore */ }
            }
            setIsSpaceUnlocked(false);
            toast({ title: "Lock turned off", description: "This space is no longer protected by PIN." });
        },
        onError: () => {
            toast({ title: "Failed to turn off lock", variant: "destructive" });
        },
    });

    // Create space relationship mutation
    const createRelationshipMutation = useMutation({
        mutationFn: async (targetSpaceId: string) => {
            if (!spaceId) throw new Error("Space ID is required");

            // Generate a simple unique ID
            const id = `${Date.now().toString(36)}${Math.random().toString(36).substring(2, 9)}`;

            const {data, error} = await supabase
                .from("space_relationships")
                .insert({
                    id,
                    source_space_id: spaceId,
                    target_space_id: targetSpaceId,
                    relationship_type: "related",
                    strength: "medium",
                })
                .select()
                .single();

            if (error) {
                console.error("Error creating relationship:", error);
                throw new Error(`Failed to create relationship: ${error.message}`);
            }

            return {
                id: data.id,
                sourceSpaceId: data.source_space_id,
                targetSpaceId: data.target_space_id,
                relationshipType: data.relationship_type,
                rationale: data.rationale,
                strength: data.strength,
                createdAt: data.created_at,
            } as SpaceRelationship;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["/api/spaces", spaceId, "relationships"]});
            toast({title: "Space Linked", description: "Spaces are now connected"});
        },
        onError: () => {
            toast({title: "Link Failed", description: "Could not link spaces", variant: "destructive"});
        },
    });

    // Delete space relationship mutation
    const deleteRelationshipMutation = useMutation({
        mutationFn: async (relationshipId: string) => {
            const {error} = await supabase
                .from("space_relationships")
                .delete()
                .eq("id", relationshipId);

            if (error) {
                console.error("Error deleting relationship:", error);
                throw new Error(`Failed to delete relationship: ${error.message}`);
            }

            return true;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["/api/spaces", spaceId, "relationships"]});
            toast({title: "Link Removed", description: "Spaces are no longer connected"});
        },
        onError: () => {
            toast({title: "Unlink Failed", description: "Could not remove link", variant: "destructive"});
        },
    });

    // Queue for serializing source mutations to prevent race conditions
    const sourceMutationQueue = useRef<Promise<any>>(Promise.resolve());
    const [isSourceMutationPending, setIsSourceMutationPending] = useState(false);

    // Queue for serializing thread mutations to prevent race conditions
    const threadMutationQueue = useRef<Promise<any>>(Promise.resolve());
    const [isThreadMutationPending, setIsThreadMutationPending] = useState(false);

    // Serialized link source mutation - updates source's linkedSpaceIds array
    const linkSourceMutation = useMutation({
        mutationFn: async (sourceId: string) => {
            return new Promise((resolve, reject) => {
                sourceMutationQueue.current = sourceMutationQueue.current.then(async () => {
                    try {
                        setIsSourceMutationPending(true);
                        if (!spaceId) throw new Error("Space ID is required");
                        const source = await sourcesService.getSource(sourceId);
                        if (!source) throw new Error("Source not found");
                        const currentLinkedSpaceIds = (source as any).linkedSpaceIds || [];
                        if (currentLinkedSpaceIds.includes(spaceId)) {
                            setIsSourceMutationPending(false);
                            resolve(source);
                            return;
                        }
                        const result = await sourcesService.updateSource(sourceId, {
                            linkedSpaceIds: [...currentLinkedSpaceIds, spaceId],
                        } as any);
                        if (!result) throw new Error("Failed to update source");
                        setIsSourceMutationPending(false);
                        resolve(result);
                    } catch (err) {
                        setIsSourceMutationPending(false);
                        reject(err);
                    }
                });
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["/api/sources", spaceId]});
            queryClient.invalidateQueries({queryKey: ["/api/sources"]});
            toast({title: "Source Linked", description: "Source added to this space"});
        },
        onError: () => {
            toast({title: "Link Failed", description: "Could not link source", variant: "destructive"});
        },
    });

    // Serialized unlink source mutation - removes spaceId from source's linkedSpaceIds array
    const unlinkSourceMutation = useMutation({
        mutationFn: async (sourceId: string) => {
            return new Promise((resolve, reject) => {
                sourceMutationQueue.current = sourceMutationQueue.current.then(async () => {
                    try {
                        setIsSourceMutationPending(true);
                        if (!spaceId) throw new Error("Space ID is required");
                        const source = await sourcesService.getSource(sourceId);
                        if (!source) throw new Error("Source not found");
                        const currentLinkedSpaceIds = (source as any).linkedSpaceIds || [];
                        if (!currentLinkedSpaceIds.includes(spaceId)) {
                            setIsSourceMutationPending(false);
                            resolve(source);
                            return;
                        }
                        const result = await sourcesService.updateSource(sourceId, {
                            linkedSpaceIds: currentLinkedSpaceIds.filter((id: string) => id !== spaceId),
                        } as any);
                        if (!result) throw new Error("Failed to update source");
                        setIsSourceMutationPending(false);
                        resolve(result);
                    } catch (err) {
                        setIsSourceMutationPending(false);
                        reject(err);
                    }
                });
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["/api/sources", spaceId]});
            queryClient.invalidateQueries({queryKey: ["/api/sources"]});
            toast({title: "Source Unlinked", description: "Source removed from this space"});
        },
        onError: (e: any) => {
            console.log(e);
            toast({title: "Unlink Failed", description: "Could not unlink source", variant: "destructive"});
        },
    });

    // Serialized link thread mutation - updates thread's linkedSpaceIds array
    const linkThreadMutation = useMutation({
        mutationFn: async (threadId: string) => {
            return new Promise((resolve, reject) => {
                threadMutationQueue.current = threadMutationQueue.current.then(async () => {
                    try {
                        setIsThreadMutationPending(true);
                        if (!spaceId) throw new Error("Space ID is required");
                        const thread = await chatThreadsService.getChatThread(
                            threadId,
                            currentWorkspaceId ?? null,
                            spaceId
                        );
                        if (!thread) throw new Error("Thread not found");
                        const currentLinkedSpaceIds = thread.linkedSpaceIds || [];
                        if (currentLinkedSpaceIds.includes(spaceId)) {
                            setIsThreadMutationPending(false);
                            resolve(thread);
                            return;
                        }
                        const result = await chatThreadsService.updateChatThread(
                            threadId,
                            {
                                linkedSpaceIds: [...currentLinkedSpaceIds, spaceId],
                            },
                            currentWorkspaceId ?? null,
                            spaceId
                        );
                        if (!result) throw new Error("Failed to update thread");
                        setIsThreadMutationPending(false);
                        resolve(result);
                    } catch (err) {
                        setIsThreadMutationPending(false);
                        reject(err);
                    }
                });
            });
        },
        onSuccess: async () => {
            // Refetch immediately to ensure consistency
            await Promise.all([
                queryClient.refetchQueries({ queryKey: ["/api/spaces", spaceId, "threads"] }),
                queryClient.refetchQueries({ queryKey: ['/api/chat-thread-summaries'], exact: false }),
                queryClient.refetchQueries({ queryKey: ["/api/chat-threads"], exact: false }),
            ]);
            toast({title: "Thread Linked", description: "Thread added to this space"});
        },
        onError: () => {
            toast({title: "Link Failed", description: "Could not link thread", variant: "destructive"});
        },
    });

    // Serialized unlink thread mutation - removes spaceId from thread's linkedSpaceIds array
    const unlinkThreadMutation = useMutation({
        mutationFn: async (threadId: string) => {
            return new Promise((resolve, reject) => {
                threadMutationQueue.current = threadMutationQueue.current.then(async () => {
                    try {
                        setIsThreadMutationPending(true);
                        if (!spaceId) throw new Error("Space ID is required");
                        const thread = await chatThreadsService.getChatThread(
                            threadId,
                            currentWorkspaceId ?? null,
                            spaceId
                        );
                        if (!thread) throw new Error("Thread not found");
                        const currentLinkedSpaceIds = thread.linkedSpaceIds || [];
                        if (!currentLinkedSpaceIds.includes(spaceId)) {
                            setIsThreadMutationPending(false);
                            resolve(thread);
                            return;
                        }
                        const result = await chatThreadsService.updateChatThread(
                            threadId,
                            {
                                linkedSpaceIds: currentLinkedSpaceIds.filter((id: string) => id !== spaceId),
                            },
                            currentWorkspaceId ?? null,
                            spaceId
                        );
                        if (!result) throw new Error("Failed to update thread");
                        setIsThreadMutationPending(false);
                        resolve(result);
                    } catch (err) {
                        setIsThreadMutationPending(false);
                        reject(err);
                    }
                });
            });
        },
        onSuccess: async () => {
            // Refetch immediately to ensure consistency
            await Promise.all([
                queryClient.refetchQueries({ queryKey: ["/api/spaces", spaceId, "threads"] }),
                queryClient.refetchQueries({ queryKey: ['/api/chat-thread-summaries'], exact: false }),
                queryClient.refetchQueries({ queryKey: ["/api/chat-threads"], exact: false }),
            ]);
            toast({title: "Thread Unlinked", description: "Thread removed from this space"});
        },
        onError: (e: any) => {
            console.log(e);
            toast({title: "Unlink Failed", description: "Could not unlink thread", variant: "destructive"});
        },
    });

    // Rename thread mutation
    const renameThreadMutation = useMutation({
        mutationFn: async ({threadId, title}: {threadId: string; title: string}) => {
            return await chatThreadsService.updateChatThread(
                threadId,
                { title },
                currentWorkspaceId ?? null,
                spaceId ?? null
            );
        },
        onSuccess: async () => {
            // Refetch immediately to ensure consistency
            await Promise.all([
                queryClient.refetchQueries({ queryKey: ["/api/spaces", spaceId, "threads"] }),
                queryClient.refetchQueries({ queryKey: ['/api/chat-thread-summaries'], exact: false }),
                queryClient.refetchQueries({ queryKey: ["/api/chat-threads"], exact: false }),
            ]);
            toast({title: "Thread Renamed", description: "Thread title updated"});
            setIsRenameThreadDialogOpen(false);
            setThreadToRename(null);
            setNewThreadTitle("");
        },
        onError: () => {
            toast({title: "Rename Failed", description: "Could not rename thread", variant: "destructive"});
        },
    });

    // Delete thread mutation
    const deleteThreadMutation = useMutation({
        mutationFn: async (threadId: string) => {
            return await chatThreadsService.deleteChatThread(threadId, spaceId ?? null);
        },
        onSuccess: async () => {
            // Optimistically remove from cache
            queryClient.setQueriesData<ThreadSummary[]>(
                { queryKey: ['/api/chat-thread-summaries'], exact: false },
                (oldData) => {
                    if (!oldData) return oldData;
                    return oldData.filter(thread => thread.id !== threadToRename?.id);
                }
            );

            // Refetch immediately to ensure consistency
            await Promise.all([
                queryClient.refetchQueries({ queryKey: ["/api/spaces", spaceId, "threads"] }),
                queryClient.refetchQueries({ queryKey: ['/api/chat-thread-summaries'], exact: false }),
                queryClient.refetchQueries({ queryKey: ["/api/chat-threads"], exact: false }),
            ]);
            toast({title: "Thread Deleted", description: "Thread has been removed"});
        },
        onError: () => {
            toast({title: "Delete Failed", description: "Could not delete thread", variant: "destructive"});
        },
    });

    // Handle thread rename
    const handleRenameThread = (thread: {id: string; title?: string | null}) => {
        setThreadToRename({id: thread.id, title: thread.title || "Untitled"});
        setNewThreadTitle(thread.title || "");
        setIsRenameThreadDialogOpen(true);
    };

    // Handle thread delete with confirmation
    const handleDeleteThread = (threadId: string, threadTitle?: string | null) => {
        if (confirm(`Are you sure you want to delete "${threadTitle || "Untitled"}"? This action cannot be undone.`)) {
            deleteThreadMutation.mutate(threadId);
        }
    };

    // Thread linking mutations
    const updateThreadSpacesMutation = useMutation({
        mutationFn: async ({threadId, spaceIds}: {threadId: string; spaceIds: string[]}) => {
            return await chatThreadsService.updateChatThread(
                threadId,
                { linkedSpaceIds: spaceIds },
                currentWorkspaceId ?? null,
                spaceId ?? null
            );
        },
        onSuccess: async (_, variables) => {
            // Optimistically update the cache immediately to prevent UI flicker
            queryClient.setQueriesData<ThreadSummary[]>(
                { queryKey: ['/api/chat-thread-summaries'], exact: false },
                (oldData) => {
                    if (!oldData) return oldData;
                    return oldData.map(thread =>
                        thread.id === variables.threadId
                          ? { ...thread, linkedSpaceIds: variables.spaceIds }
                          : thread
                    );
                }
            );

            // Refetch immediately to ensure consistency
            await Promise.all([
                queryClient.refetchQueries({ queryKey: ["/api/spaces", spaceId, "threads"] }),
                queryClient.refetchQueries({ queryKey: ['/api/chat-thread-summaries'], exact: false }),
                queryClient.refetchQueries({ queryKey: ["/api/chat-threads"], exact: false }),
            ]);

            // Invalidate related queries for other components
            queryClient.invalidateQueries({queryKey: ["/api/spaces", spaceId, "topics"]});
            queryClient.invalidateQueries({queryKey: ["/api/topics"]});

            toast({title: "Spaces Updated", description: "Thread linked spaces updated"});
        },
        onError: async (error: any) => {
            // Refetch on error to restore correct state
            await queryClient.refetchQueries({ queryKey: ['/api/chat-thread-summaries'], exact: false });
            toast({title: "Update Failed", description: error?.message || "Could not update linked spaces", variant: "destructive"});
        },
    });

    const updateThreadTopicMutation = useMutation({
        mutationFn: async ({threadId, topicId}: {threadId: string; topicId: string | null}) => {
            return await chatThreadsService.updateChatThread(
                threadId,
                { topicId },
                currentWorkspaceId ?? null,
                spaceId ?? null
            );
        },
        onSuccess: async (_, variables) => {
            // Optimistically update the cache immediately to prevent UI flicker
            queryClient.setQueriesData<ThreadSummary[]>(
                { queryKey: ['/api/chat-thread-summaries'], exact: false },
                (oldData) => {
                    if (!oldData) return oldData;
                    return oldData.map(thread =>
                        thread.id === variables.threadId
                          ? { ...thread, topicId: variables.topicId || null }
                          : thread
                    );
                }
            );

            // Refetch immediately to ensure consistency
            await Promise.all([
                queryClient.refetchQueries({ queryKey: ["/api/spaces", spaceId, "threads"] }),
                queryClient.refetchQueries({ queryKey: ['/api/chat-thread-summaries'], exact: false }),
                queryClient.refetchQueries({ queryKey: ["/api/chat-threads"], exact: false }),
            ]);

            // Invalidate related queries for other components
            queryClient.invalidateQueries({queryKey: ["/api/spaces", spaceId, "topics"]});
            queryClient.invalidateQueries({queryKey: ["/api/topics"]});
            if (variables.topicId) {
                queryClient.invalidateQueries({queryKey: ["/api/topics", variables.topicId, "threads"]});
            }

            toast({title: "Topic Updated", description: "Thread topic updated"});
        },
        onError: async (error: any) => {
            // Refetch on error to restore correct state
            await queryClient.refetchQueries({ queryKey: ['/api/chat-thread-summaries'], exact: false });
            toast({title: "Update Failed", description: error?.message || "Could not update thread topic", variant: "destructive"});
        },
    });

    const updateThreadTagsMutation = useMutation({
        mutationFn: async ({threadId, tags}: {threadId: string; tags: string[]}) => {
            return await chatThreadsService.updateChatThread(
                threadId,
                { tags },
                currentWorkspaceId ?? null,
                spaceId ?? null
            );
        },
        onSuccess: async (_, variables) => {
            // Optimistically update the cache immediately to prevent UI flicker
            queryClient.setQueriesData<ThreadSummary[]>(
                { queryKey: ['/api/chat-thread-summaries'], exact: false },
                (oldData) => {
                    if (!oldData) return oldData;
                    return oldData.map(thread =>
                        thread.id === variables.threadId
                          ? { ...thread, tags: variables.tags }
                          : thread
                    );
                }
            );

            // Refetch immediately to ensure consistency
            await Promise.all([
                queryClient.refetchQueries({ queryKey: ["/api/spaces", spaceId, "threads"] }),
                queryClient.refetchQueries({ queryKey: ['/api/chat-thread-summaries'], exact: false }),
                queryClient.refetchQueries({ queryKey: ["/api/chat-threads"], exact: false }),
            ]);

            // Invalidate related queries for other components
            queryClient.invalidateQueries({queryKey: ["/api/spaces", spaceId, "topics"]});
            queryClient.invalidateQueries({queryKey: ["/api/topics"]});

            toast({title: "Tags Updated", description: "Thread tags updated"});
        },
        onError: async (error: any) => {
            // Refetch on error to restore correct state
            await queryClient.refetchQueries({ queryKey: ['/api/chat-thread-summaries'], exact: false });
            toast({title: "Update Failed", description: error?.message || "Could not update thread tags", variant: "destructive"});
        },
    });

    // Handle thread linking dialog
    const handleLinkThread = (thread: ChatThread | any) => {
        setThreadToLink({
            id: thread.id,
            linkedSpaceIds: thread.linkedSpaceIds || [],
            topicId: thread.topicId || null,
            tags: thread.tags || [],
        });
    };

    // Create source mutation
    const createSourceMutation = useMutation({
        mutationFn: async (newSource: {
            title: string;
            content: string;
            type: string;
            url?: string;
            topics: string[];
            tags: string[];
            attachments?: { id: string; filename: string; url: string }[];
        }): Promise<Source> => {
            const sourceData: any = {
                title: newSource.title,
                content: newSource.content,
                type: newSource.type,
                url: newSource.url,
                topics: newSource.topics,
                tags: newSource.tags,
                attachments: newSource.attachments,
                // Auto-link to this space by including spaceId in linkedSpaceIds
                linkedSpaceIds: spaceId ? [spaceId] : [],
            };
            console.log(sourceData);
            return await sourcesService.createSource(sourceData, currentWorkspaceId ?? null);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['/api/sources']});
            queryClient.invalidateQueries({queryKey: ["/api/sources", spaceId]});
            toast({title: "Source Created", description: "New source added and linked to this space"});
            // Trigger first_source milestone for new users
            if (!hasMilestone('first_source')) {
                completeMilestone('first_source');
            }
            resetSourceDialog();
            setIsAddSourceDialogOpen(false);
        },
        onError: (e: any) => {
            console.log('err:', e);
            toast({title: "Creation Failed", description: "Could not create source", variant: "destructive"});
        },
    });

    // Reset source dialog state
    const resetSourceDialog = () => {
        setSourceType("text");
        setNewSourceTitle("");
        setNewSourceContent("");
        setNewSourceUrl("");
        setNewSourceTags([]);
        setNewSourceTopics([]);
        setSourceUploadFiles([]);
    };

    // Handle source file upload
    const handleSourceFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files?.length) return;

        const toBase64 = (file: File): Promise<string> =>
            new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                    const result = reader.result as string;
                    const base64 = result.split(",")[1] || "";
                    resolve(base64);
                };
                reader.onerror = (err) => reject(err);
                reader.readAsDataURL(file);
            });

        for (const file of Array.from(files)) {
            try {
                const content = await toBase64(file);
                const response = await apiRequest("POST", "/api/files", {
                    filename: file.name,
                    content,
                    mimeType: file.type || "application/octet-stream",
                });
                const data = await response.json() as { id: string; filename: string; url: string };
                setSourceUploadFiles((prev) => [
                    ...prev,
                    {id: data.id, filename: data.filename, url: data.url},
                ]);
            } catch (error) {
                console.error("File upload failed:", error);
                toast({title: "Upload Failed", description: "Could not upload file", variant: "destructive"});
            }
        }
        event.target.value = "";
    };

    // Handle add source submit
    const handleAddSource = () => {
        if (!newSourceTitle.trim()) {
            toast({title: "Title Required", description: "Please enter a title", variant: "destructive"});
            return;
        }

        let content = newSourceContent;
        if (sourceType === "upload" || sourceType === "google-doc") {
            if (sourceUploadFiles.length === 0) {
                toast({
                    title: "Files Required",
                    description: "Please upload or import at least one file",
                    variant: "destructive"
                });
                return;
            }
            content = `Imported files: ${sourceUploadFiles.map(f => f.filename).join(", ")}`;
        }

        if (sourceType === "url" && !newSourceUrl.trim()) {
            toast({title: "URL Required", description: "Please enter a URL", variant: "destructive"});
            return;
        }

        createSourceMutation.mutate({
            title: newSourceTitle,
            content,
            type: sourceType,
            url: newSourceUrl || undefined,
            topics: newSourceTopics,
            tags: newSourceTags,
            attachments: sourceUploadFiles,
        });
    };

    // Cleanup for hero image dialog
    const cleanupHeroImageDialog = () => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        if (fileInputRef.current) fileInputRef.current.value = "";
        setPreviewUrl("");
        setUploadedImageUrl("");
        setIsUploading(false);
    };

    // Upload hero image mutation
    const uploadHeroMutation = useMutation({
        mutationFn: async (imageUrl: string) => {
            const existingHeroImages = assets.filter(a => a.assetType === "hero_image" && a.isPrimary === "true");
            for (const asset of existingHeroImages) {
                await apiRequest("PATCH", `/api/space-assets/${asset.id}`, {isPrimary: "false"});
            }
            return await apiRequest("POST", `/api/spaces/${spaceId}/assets`, {
                assetType: "hero_image",
                url: imageUrl,
                isPrimary: "true",
                metadata: {},
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["/api/spaces", spaceId, "assets"]});
            toast({title: "Hero Image Updated"});
            cleanupHeroImageDialog();
            setIsUploadDialogOpen(false);
        },
        onError: (error: any) => {
            cleanupHeroImageDialog();
            toast({title: "Upload Failed", description: error.message, variant: "destructive"});
        },
    });

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            toast({title: "Invalid File", description: "Please select an image file", variant: "destructive"});
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
        }

        if (previewUrl) URL.revokeObjectURL(previewUrl);
        const localPreviewUrl = URL.createObjectURL(file);
        setPreviewUrl(localPreviewUrl);
        setIsUploading(true);

        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const base64String = e.target?.result as string;
                const base64Content = base64String.split(',')[1];
                const response = await fetch("/api/files", {
                    method: "POST",
                    headers: {"Content-Type": "application/json"},
                    body: JSON.stringify({filename: file.name, content: base64Content, mimeType: file.type}),
                });
                if (!response.ok) throw new Error("Upload failed");
                const data = await response.json();
                setUploadedImageUrl(data.url);
                setIsUploading(false);
            } catch (error: any) {
                URL.revokeObjectURL(localPreviewUrl);
                if (fileInputRef.current) fileInputRef.current.value = "";
                setIsUploading(false);
                setPreviewUrl("");
                toast({title: "Upload Failed", description: error.message, variant: "destructive"});
            }
        };
        reader.onerror = () => {
            URL.revokeObjectURL(localPreviewUrl);
            if (fileInputRef.current) fileInputRef.current.value = "";
            setIsUploading(false);
            setPreviewUrl("");
            toast({title: "Upload Failed", variant: "destructive"});
        };
        reader.readAsDataURL(file);
    };

    const handleAddGoal = () => {
        if (!newGoal.trim()) {
            toast({title: "Invalid Goal", description: "Please enter a goal", variant: "destructive"});
            return;
        }
        if (!space) return;
        const updatedGoals = [...(space.goals || []), newGoal.trim()];
        updateSpaceMutation.mutate({goals: updatedGoals}, {
            onSuccess: () => {
                toast({title: "Goal Added", description: "Your goal has been added to this space"});
                setNewGoal("");
                setIsAddingGoal(false);
            }
        });
    };

    const handleRemoveGoal = (index: number) => {
        if (!space?.goals) return;
        const updatedGoals = space.goals.filter((_, i) => i !== index);
        updateSpaceMutation.mutate({goals: updatedGoals}, {
            onSuccess: () => {
                toast({title: "Goal Removed", description: "The goal has been removed"});
            }
        });
    };

    const handleSaveEdit = () => {
        updateSpaceMutation.mutate({name: editName, description: editDescription});
    };

    const handleEditLens = () => {
        const currentLens = (space?.lens as SpaceLens) || {purpose: "", priorities: "", details: ""};
        setEditLens(currentLens);
        setIsEditingLens(true);
    };

    const handleSaveLens = () => {
        updateSpaceMutation.mutate({lens: editLens}, {
            onSuccess: () => {
                toast({title: "Lens Updated", description: "Your space lens has been saved"});
                setIsEditingLens(false);
            }
        });
    };

    const currentLens = (space?.lens as SpaceLens) || {purpose: "", priorities: "", details: ""};
    const hasLensContent = currentLens.purpose || currentLens.priorities || currentLens.details;

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (!space) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="text-center">
                    <p className="text-lg font-medium mb-2">Space not found</p>
                    <Button onClick={() => setLocation("/spaces")} data-testid="button-back-to-spaces">
                        <ArrowLeft className="w-4 h-4 mr-2"/>
                        Back to Spaces
                    </Button>
                </div>
            </div>
        );
    }

    // Show locked overlay ONLY if space is locked AND user is the owner
    // Shared members should not be asked for PIN (PIN is only for the space owner)
    const shouldShowLock = (space.isLocked === true || space.isLocked === "true") && 
                          !isSpaceUnlocked && 
                          isSpaceOwner;
    
    if (shouldShowLock) {
        return (
            <div className="h-screen flex flex-col">
                <div className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-10">
                    <div className="flex items-center gap-2 px-3 py-1.5">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setLocation("/spaces")}
                            data-testid="button-back"
                        >
                            <ArrowLeft className="w-4 h-4"/>
                        </Button>
                        <div className="min-w-0">
                            <div className="flex items-center gap-2 min-w-0">
                                <h1 className="text-base font-semibold truncate" data-testid="text-space-name">
                                    {space.name}
                                </h1>
                                {mySpaceRole && (
                                    <Badge
                                        variant={spaceRoleBadgeVariant(mySpaceRole)}
                                        className="text-[10px] px-1.5 py-0 h-4 shrink-0"
                                        data-testid="badge-my-space-role"
                                    >
                                        {formatSpaceRoleLabel(mySpaceRole)}
                                    </Badge>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="flex-1">
                    <LockedEntityOverlay
                        entityType="space"
                        onUnlock={() => setShowSpacePinDialog(true)}
                        dialogControlledByParent
                    />
                </div>
                <PinVerificationDialog
                    open={showSpacePinDialog}
                    onOpenChange={setShowSpacePinDialog}
                    onSuccess={() => {
                        try {
                            if (spaceId && typeof window !== "undefined") {
                                sessionStorage.setItem(UNLOCK_KEY(spaceId), "1");
                            }
                        } catch {
                            // ignore
                        }
                        setIsSpaceUnlocked(true);
                        setShowSpacePinDialog(false);
                    }}
                />
            </div>
        );
    }

    return (
        <div className="h-full w-full min-w-0 flex flex-col">
            {/* Compact Header */}
            <div
                className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-10">
                <div className="flex items-center justify-between gap-2 px-3 py-1.5 pr-20">
                    <div className="flex items-center gap-2 min-w-0">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setLocation("/spaces")}
                            data-testid="button-back"
                        >
                            <ArrowLeft className="w-4 h-4"/>
                        </Button>
                        <div className="min-w-0">
                            <div className="flex items-center gap-2 min-w-0">
                                <h1 className="text-base font-semibold truncate" data-testid="text-space-name">
                                    {space.name}
                                </h1>
                                {mySpaceRole && (
                                    <Badge
                                        variant={spaceRoleBadgeVariant(mySpaceRole)}
                                        className="text-[10px] px-1.5 py-0 h-4 shrink-0"
                                        data-testid="badge-my-space-role"
                                    >
                                        {formatSpaceRoleLabel(mySpaceRole)}
                                    </Badge>
                                )}
                            </div>
                            {space.description && (
                                <p className="text-xs text-muted-foreground truncate max-w-md">
                                    {space.description}
                                </p>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <Button
                            size="sm"
                            onClick={() => setLocation(`/chat?spaceId=${spaceId}`)}
                            data-testid="button-start-chat"
                        >
                            <MessageCircle className="w-4 h-4 mr-0 sm:mr-2"/>
                            <span className="hidden sm:inline">Chat</span>
                        </Button>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="outline" size="icon" data-testid="button-space-menu">
                                    <MoreVertical className="w-4 h-4"/>
                                </Button>
                            </DropdownMenuTrigger>
<DropdownMenuContent align="end" className="w-72">
    <DropdownMenuItem onClick={()=> { setEditName(space?.name || ""); setEditDescription(space?.description || ""); setIsEditingSpace(true); }} data-testid="menu-rename-space">
        <Pencil className="w-4 h-4 mr-2" /> Rename Space </DropdownMenuItem>
    <DropdownMenuItem onSelect={(e)=> e.preventDefault()} className="flex items-center justify-between gap-4 cursor-default" >
        <div className="flex items-start gap-2 min-w-0">
            <Brain className="w-4 h-4 mt-0.5 shrink-0" />
            <div className="min-w-0">
                <div>Available to AI</div>
                <div className="text-xs text-muted-foreground whitespace-normal"> When enabled, this space's content can be searched by AI </div>
            </div>
        </div>
        <Switch checked={(space?.includeInRag ?? true) !== false} onCheckedChange={(checked)=> { updateSpaceMutation.mutate({ includeInRag: checked } as Partial<MastermindSpace>); }} onClick={(e) => e.stopPropagation()} data-testid="switch-space-include-in-rag" /> </DropdownMenuItem> {(space?.isLocked === true || space?.isLocked === "true") && isSpaceUnlocked && (
    <>
        <DropdownMenuItem onClick={()=> { if (spaceId && typeof window !== "undefined") { try { sessionStorage.removeItem(UNLOCK_KEY(spaceId)); } catch { /* ignore */ } } setIsSpaceUnlocked(false); toast({ title: "Locked again", description: "This space will require PIN on next visit."
            }); }} data-testid="button-lock-again" >
            <Lock className="w-4 h-4 mr-2" /> Lock Space (require PIN next time) </DropdownMenuItem>
        <DropdownMenuItem onClick={()=> turnLockOffMutation.mutate()} disabled={turnLockOffMutation.isPending} data-testid="button-turn-lock-off" >
            <LockOpen className="w-4 h-4 mr-2" /> {turnLockOffMutation.isPending ? "Turning off…" : "Remove PIN Lock from Space"} </DropdownMenuItem>
        </> )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={()=> setIsShareDialogOpen(true)} data-testid="menu-share-space">
            <Share2 className="w-4 h-4 mr-2" /> Share Space </DropdownMenuItem>
        <DropdownMenuItem onClick={()=> setIsUploadDialogOpen(true)} data-testid="menu-upload-hero-image">
            <Upload className="w-4 h-4 mr-2" /> Upload Hero Image </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={()=> { setDeleteSpaceConfirmText(""); setIsDeleteSpaceDialogOpen(true); }} className="text-destructive focus:text-destructive" data-testid="menu-delete-space" >
            <Trash2 className="w-4 h-4 mr-2" /> Delete Space </DropdownMenuItem>
</DropdownMenuContent>
                        </DropdownMenu>
                        {/* Mobile menu button */}
                        <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
                            <SheetTrigger asChild>
                                <Button
                                    variant="outline"
                                    size="icon"
                                    className="md:hidden"
                                    data-testid="button-mobile-menu"
                                >
                                    <Menu className="w-4 h-4"/>
                                </Button>
                            </SheetTrigger>
                            <SheetContent side="left" className="w-80 p-0">
                                <SheetHeader className="p-4 border-b">
                                    <SheetTitle>Space Menu</SheetTitle>
                                </SheetHeader>
                                <ScrollArea className="h-[calc(100vh-60px)]">
                                    <div className="p-4 space-y-4">
                                        {/* Quick Stats - Mobile */}
                                        <div className="grid grid-cols-2 gap-2">
                                            <Card className="p-3">
                                                <div className="text-xl font-bold">{topics.length}</div>
                                                <div className="text-xs text-muted-foreground">Topics</div>
                                            </Card>
                                            <Card className="p-3">
                                                <div className="text-xl font-bold">{recentThreads.length}</div>
                                                <div className="text-xs text-muted-foreground">Threads</div>
                                            </Card>
                                            <Card className="p-3">
                                                <div className="text-xl font-bold">{relationships.length}</div>
                                                <div className="text-xs text-muted-foreground">Links</div>
                                            </Card>
                                        </div>

                                        {/* Quick Actions - Mobile */}
                                        <div>
                                            <h4 className="text-sm font-medium mb-2 flex items-center gap-1">
                                                <Zap className="w-4 h-4"/>
                                                Quick Actions
                                            </h4>
                                            <div className="space-y-1">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="w-full justify-start"
                                                    onClick={() => {
                                                        setLocation(`/chat?spaceId=${spaceId}`);
                                                        setIsMobileMenuOpen(false);
                                                    }}
                                                    data-testid="mobile-action-start-chat"
                                                >
                                                    <MessageCircle className="w-4 h-4 mr-2"/>
                                                    Start AI Chat
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="w-full justify-start"
                                                    onClick={() => {
                                                        setActiveTab("documents");
                                                        setIsMobileMenuOpen(false);
                                                    }}
                                                    data-testid="mobile-action-new-document"
                                                >
                                                    <FileText className="w-4 h-4 mr-2"/>
                                                    New Document
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="w-full justify-start"
                                                    onClick={() => {
                                                        setActiveTab("topics");
                                                        setIsMobileMenuOpen(false);
                                                    }}
                                                    data-testid="mobile-action-add-topic"
                                                >
                                                    <FolderOpen className="w-4 h-4 mr-2"/>
                                                    Add Topic
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="w-full justify-start"
                                                    onClick={() => {
                                                        setActiveTab("sources");
                                                        setIsMobileMenuOpen(false);
                                                    }}
                                                    data-testid="mobile-action-add-source"
                                                >
                                                    <BookOpen className="w-4 h-4 mr-2"/>
                                                    Add Source
                                                </Button>
                                            </div>
                                        </div>

                                        {/* AI Agents - Mobile */}
                                        <div>
                                            <h4 className="text-sm font-medium mb-2 flex items-center gap-1">
                                                <Bot className="w-4 h-4"/>
                                                AI Agents ({agents.length})
                                            </h4>
                                            {agents.length > 0 ? (
                                                <div className="space-y-1">
                                                    {agents.slice(0, 5).map((agent: any) => (
                                                        <div
                                                            key={agent.id}
                                                            className="flex items-center gap-2 p-2 rounded-md bg-muted text-xs cursor-pointer hover-elevate"
                                                            onClick={() => {
                                                                setLocation(`/chat?spaceId=${spaceId}&agentId=${agent.id}`);
                                                                setIsMobileMenuOpen(false);
                                                            }}
                                                            data-testid={`mobile-agent-${agent.id}`}
                                                        >
                                                            <Bot className="w-3 h-3 text-primary"/>
                                                            <span className="truncate">{agent.name}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className="text-xs text-muted-foreground">No agents configured</p>
                                            )}
                                        </div>

                                        {/* Recent Threads - Mobile */}
                                        <div>
                                            <h4 className="text-sm font-medium mb-2 flex items-center gap-1">
                                                <MessageSquare className="w-4 h-4"/>
                                                Recent Threads ({recentThreads.length})
                                            </h4>
                                            {recentThreads.length > 0 ? (
                                                <div className="space-y-1">
                                                    {recentThreads.slice(0, 5).map((thread) => (
                                                        <div
                                                            key={thread.id}
                                                            className="flex items-center gap-2 p-2 rounded-md text-xs cursor-pointer hover-elevate group"
                                                            onClick={() => {
                                                                setLocation(
                                                                    `/chat?threadId=${thread.id}${spaceId ? `&spaceId=${spaceId}` : ""}`
                                                                );
                                                                setIsMobileMenuOpen(false);
                                                            }}
                                                            data-testid={`mobile-thread-${thread.id}`}
                                                        >
                                                            <MessageSquare className="w-3 h-3 text-muted-foreground flex-shrink-0"/>
                                                            <span className="truncate flex-1">{thread.title || "Untitled"}</span>
                                                            <DropdownMenu>
                                                                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        className="h-6 w-6 flex-shrink-0"
                                                                        data-testid={`button-mobile-thread-menu-${thread.id}`}
                                                                    >
                                                                        <MoreVertical className="w-3 h-3"/>
                                                                    </Button>
                                                                </DropdownMenuTrigger>
                                                                <DropdownMenuContent align="end">
                                                                    <DropdownMenuItem onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleRenameThread(thread);
                                                                        setIsMobileMenuOpen(false);
                                                                    }}>
                                                                        <Pencil className="w-3 h-3 mr-2"/>
                                                                        Rename
                                                                    </DropdownMenuItem>
                                                                    <DropdownMenuItem
                                                                        className="text-destructive"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleDeleteThread(thread.id, thread.title);
                                                                        }}
                                                                    >
                                                                        <Trash2 className="w-3 h-3 mr-2"/>
                                                                        Delete
                                                                    </DropdownMenuItem>
                                                                </DropdownMenuContent>
                                                            </DropdownMenu>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className="text-xs text-muted-foreground">No recent activity</p>
                                            )}
                                        </div>

                                        {/* Goals - Mobile */}
                                        <div>
                                            <div className="flex items-center justify-between mb-2">
                                                <h4 className="text-sm font-medium flex items-center gap-1">
                                                    <Target className="w-4 h-4"/>
                                                    Goals ({space.goals?.length || 0})
                                                </h4>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-6 w-6"
                                                    onClick={() => {
                                                        setIsAddingGoal(true);
                                                        setIsMobileMenuOpen(false);
                                                    }}
                                                    data-testid="mobile-button-add-goal"
                                                >
                                                    <Plus className="w-3 h-3"/>
                                                </Button>
                                            </div>
                                            {space.goals && space.goals.length > 0 ? (
                                                <div className="space-y-1">
                                                    {space.goals.map((goal, index) => (
                                                        <div key={index}
                                                             className="flex items-start gap-2 p-2 rounded-md bg-muted text-xs">
                                                            <Circle
                                                                className="w-3 h-3 mt-0.5 text-primary flex-shrink-0"/>
                                                            <span className="flex-1">{goal}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className="text-xs text-muted-foreground">No goals set</p>
                                            )}
                                        </div>
                                    </div>
                                </ScrollArea>
                            </SheetContent>
                        </Sheet>
                    </div>
                </div>
            </div>

            {/* Main 3-Column Layout */}
            <div className="flex-1 overflow-hidden min-w-0">
                <div className="h-full flex min-w-0">
                    {/* Left Column - Stats & Goals (Collapsible) */}
                    <div className={`hidden md:flex flex-col border-r bg-muted/30 overflow-hidden transition-all duration-300 ${isSidebarCollapsed ? 'w-12' : 'w-64'}`}>
                        {/* Collapse/Expand Toggle Button */}
                        <div className="p-2 border-b flex justify-center">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={toggleSidebarCollapsed}
                                className="h-8 w-8 p-0"
                                title={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                                data-testid="button-toggle-space-sidebar"
                            >
                                {isSidebarCollapsed ? (
                                    <ChevronRight className="h-4 w-4" />
                                ) : (
                                    <ChevronDown className="h-4 w-4" />
                                )}
                            </Button>
                        </div>

                        {/* Sidebar Content - only show when expanded */}
                        {!isSidebarCollapsed && (
                        <ScrollArea className="flex-1">
                            <div className="p-4 space-y-4">
                                {/* Quick Stats */}
                                <div className="grid grid-cols-2 gap-2">
                                    <Card className="p-3">
                                        <div className="text-2xl font-bold">{topics.length}</div>
                                        <div className="text-xs text-muted-foreground">Topics</div>
                                    </Card>
                                    <Card className="p-3">
                                        <div className="text-2xl font-bold">{recentThreads.length}</div>
                                        <div className="text-xs text-muted-foreground">Threads</div>
                                    </Card>
                                    <Card className="p-3">
                                        <div className="text-2xl font-bold">{relationships.length}</div>
                                        <div className="text-xs text-muted-foreground">Links</div>
                                    </Card>
                                </div>

                                {/* Quick Actions */}
                                <Collapsible open={quickActionsOpen}
                                             onOpenChange={(v) => toggleSection("quickActions", v, setQuickActionsOpen)}>
                                    <CollapsibleTrigger
                                        className="flex items-center gap-1.5 w-full text-xs font-medium text-muted-foreground hover:text-foreground group">
                                        <ChevronDown className="w-3.5 h-3.5 group-data-[state=closed]:hidden"/>
                                        <ChevronRight className="w-3.5 h-3.5 group-data-[state=open]:hidden"/>
                                        <Zap className="w-3.5 h-3.5"/>
                                        <span>Quick Actions</span>
                                    </CollapsibleTrigger>
                                    <CollapsibleContent className="mt-2 space-y-0.5 pl-5">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="w-full justify-start h-7 text-xs"
                                            onClick={() => setLocation(`/chat?spaceId=${spaceId}`)}
                                            data-testid="action-start-chat"
                                        >
                                            <MessageCircle className="w-3 h-3 mr-2"/>
                                            Start AI Chat
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="w-full justify-start h-7 text-xs"
                                            onClick={() => setActiveTab("documents")}
                                            data-testid="action-new-document"
                                        >
                                            <FileText className="w-3 h-3 mr-2"/>
                                            New Document
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="w-full justify-start h-7 text-xs"
                                            onClick={() => setActiveTab("topics")}
                                            data-testid="action-add-topic"
                                        >
                                            <FolderOpen className="w-3 h-3 mr-2"/>
                                            Add Topic
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="w-full justify-start h-7 text-xs"
                                            onClick={() => setActiveTab("sources")}
                                            data-testid="action-add-source"
                                        >
                                            <BookOpen className="w-3 h-3 mr-2"/>
                                            Add Source
                                        </Button>
                                    </CollapsibleContent>
                                </Collapsible>

                                {/* AI Agents */}
                                <Collapsible open={aiAgentsOpen}
                                             onOpenChange={(v) => toggleSection("aiAgents", v, setAiAgentsOpen)}>
                                    <CollapsibleTrigger
                                        className="flex items-center gap-1.5 w-full text-xs font-medium text-muted-foreground hover:text-foreground group">
                                        <ChevronDown className="w-3.5 h-3.5 group-data-[state=closed]:hidden"/>
                                        <ChevronRight className="w-3.5 h-3.5 group-data-[state=open]:hidden"/>
                                        <Bot className="w-3.5 h-3.5"/>
                                        <span>AI Agents</span>
                                        <Badge variant="secondary"
                                               className="ml-auto text-[10px] px-1.5 h-4">{agents.length}</Badge>
                                    </CollapsibleTrigger>
                                    <CollapsibleContent className="mt-2 space-y-0.5 pl-5">
                                        {agents.length > 0 ? (
                                            agents.slice(0, 5).map((agent: any) => (
                                                <div
                                                    key={agent.id}
                                                    className="flex items-center gap-2 py-1.5 px-2 rounded-md text-xs cursor-pointer hover-elevate"
                                                    onClick={() => setLocation(`/chat?spaceId=${spaceId}&agentId=${agent.id}`)}
                                                >
                                                    <Bot className="w-3 h-3 text-primary"/>
                                                    <span className="truncate">{agent.name}</span>
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-xs text-muted-foreground py-1.5 px-2">No agents
                                                configured</p>
                                        )}
                                    </CollapsibleContent>
                                </Collapsible>

                                {/* Recent Threads */}
                                <Collapsible open={recentThreadsOpen}
                                             onOpenChange={(v) => toggleSection("recentThreads", v, setRecentThreadsOpen)}>
                                    <CollapsibleTrigger
                                        className="flex items-center gap-1.5 w-full text-xs font-medium text-muted-foreground hover:text-foreground group">
                                        <ChevronDown className="w-3.5 h-3.5 group-data-[state=closed]:hidden"/>
                                        <ChevronRight className="w-3.5 h-3.5 group-data-[state=open]:hidden"/>
                                        <MessageSquare className="w-3.5 h-3.5"/>
                                        <span>Recent Threads</span>
                                        <Badge variant="secondary"
                                               className="ml-auto text-[10px] px-1.5 h-4">{recentThreads.length}</Badge>
                                    </CollapsibleTrigger>
                                    <CollapsibleContent className="mt-2 space-y-0.5 pl-5">
                                        {recentThreads.length > 0 ? (
                                            recentThreads.slice(0, 5).map((thread) => (
                                                <div
                                                    key={thread.id}
                                                    className="flex items-center gap-2 py-1.5 px-2 rounded-md text-xs cursor-pointer hover-elevate group"
                                                    onClick={() =>
                                                        setLocation(
                                                            `/chat?threadId=${thread.id}${spaceId ? `&spaceId=${spaceId}` : ""}`
                                                        )
                                                    }
                                                    data-testid={`sidebar-thread-${thread.id}`}
                                                >
                                                    <MessageSquare className="w-3 h-3 text-muted-foreground flex-shrink-0"/>
                                                    <span className="truncate flex-1">{thread.title || "Untitled"}</span>
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-5 w-5 opacity-0 group-hover:opacity-100 flex-shrink-0"
                                                                data-testid={`button-thread-menu-${thread.id}`}
                                                            >
                                                                <MoreVertical className="w-3 h-3"/>
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end">
                                                            <DropdownMenuItem onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleRenameThread(thread);
                                                            }}>
                                                                <Pencil className="w-3 h-3 mr-2"/>
                                                                Rename
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleLinkThread(thread);
                                                            }}>
                                                                <Link2 className="w-3 h-3 mr-2"/>
                                                                Link to Topic
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem
                                                                className="text-destructive"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDeleteThread(thread.id, thread.title);
                                                                }}
                                                            >
                                                                <Trash2 className="w-3 h-3 mr-2"/>
                                                                Delete
                                                            </DropdownMenuItem>
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-xs text-muted-foreground py-1.5 px-2">No recent
                                                activity</p>
                                        )}
                                    </CollapsibleContent>
                                </Collapsible>

                                {/* AI Briefing */}
                                <Collapsible open={aiBriefingOpen}
                                             onOpenChange={(v) => toggleSection("aiBriefing", v, setAiBriefingOpen)}>
                                    <CollapsibleTrigger
                                        className="flex items-center gap-1.5 w-full text-xs font-medium text-muted-foreground hover:text-foreground group">
                                        <ChevronDown className="w-3.5 h-3.5 group-data-[state=closed]:hidden"/>
                                        <ChevronRight className="w-3.5 h-3.5 group-data-[state=open]:hidden"/>
                                        <Sparkles className="w-3.5 h-3.5"/>
                                        <span>AI Briefing</span>
                                    </CollapsibleTrigger>
                                    <CollapsibleContent className="mt-2 pl-5">
                                        <SpaceInsights spaceId={spaceId}/>
                                    </CollapsibleContent>
                                </Collapsible>

                                {/* Goals */}
                                <Collapsible open={goalsOpen}
                                             onOpenChange={(v) => toggleSection("goals", v, setGoalsOpen)}>
                                    <div className="flex items-center gap-1.5">
                                        <CollapsibleTrigger
                                            className="flex items-center gap-1.5 flex-1 text-xs font-medium text-muted-foreground hover:text-foreground group">
                                            <ChevronDown className="w-3.5 h-3.5 group-data-[state=closed]:hidden"/>
                                            <ChevronRight className="w-3.5 h-3.5 group-data-[state=open]:hidden"/>
                                            <Target className="w-3.5 h-3.5"/>
                                            <span>Goals</span>
                                            <Badge variant="secondary"
                                                   className="ml-auto text-[10px] px-1.5 h-4">{space.goals?.length || 0}</Badge>
                                        </CollapsibleTrigger>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-5 w-5"
                                            onClick={() => setIsAddingGoal(true)}
                                            data-testid="button-add-goal"
                                        >
                                            <Plus className="w-3 h-3"/>
                                        </Button>
                                    </div>
                                    <CollapsibleContent className="mt-2 space-y-0.5 pl-5">
                                        {space.goals && space.goals.length > 0 ? (
                                            space.goals.map((goal, index) => (
                                                <div
                                                    key={index}
                                                    className="flex items-start gap-2 py-1.5 px-2 rounded-md text-xs group"
                                                >
                                                    <Circle className="w-3 h-3 mt-0.5 text-primary flex-shrink-0"/>
                                                    <span className="flex-1 break-words">{goal}</span>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity"
                                                        onClick={() => handleRemoveGoal(index)}
                                                    >
                                                        <X className="w-2.5 h-2.5"/>
                                                    </Button>
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-xs text-muted-foreground py-1.5 px-2">No goals yet</p>
                                        )}
                                    </CollapsibleContent>
                                </Collapsible>

                                {/* Milestones */}
                                <Collapsible open={milestonesOpen}
                                             onOpenChange={(v) => toggleSection("milestones", v, setMilestonesOpen)}>
                                    <CollapsibleTrigger
                                        className="flex items-center gap-1.5 w-full text-xs font-medium text-muted-foreground hover:text-foreground group">
                                        <ChevronDown className="w-3.5 h-3.5 group-data-[state=closed]:hidden"/>
                                        <ChevronRight className="w-3.5 h-3.5 group-data-[state=open]:hidden"/>
                                        <CheckCircle2 className="w-3.5 h-3.5"/>
                                        <span>Milestones</span>
                                    </CollapsibleTrigger>
                                    <CollapsibleContent className="mt-2 pl-5">
                                        <p className="text-xs text-muted-foreground py-1.5 px-2">No milestones yet</p>
                                    </CollapsibleContent>
                                </Collapsible>

                                {/* Tags */}
                                <Collapsible open={tagsOpen}
                                             onOpenChange={(v) => toggleSection("tags", v, setTagsOpen)}>
                                    <CollapsibleTrigger
                                        className="flex items-center gap-1.5 w-full text-xs font-medium text-muted-foreground hover:text-foreground group">
                                        <ChevronDown className="w-3.5 h-3.5 group-data-[state=closed]:hidden"/>
                                        <ChevronRight className="w-3.5 h-3.5 group-data-[state=open]:hidden"/>
                                        <TagIcon className="w-3.5 h-3.5"/>
                                        <span>Tags</span>
                                    </CollapsibleTrigger>
                                    <CollapsibleContent className="mt-2 pl-5">
                                        <TagPicker
                                            entityType="mastermind_space"
                                            entityId={spaceId}
                                            onTagsChange={() => {
                                            }}
                                        />
                                        {space && (space.name || space.description) && (
                                            <SmartTagSuggestions
                                                content={space.description || ""}
                                                title={space.name}
                                                entityType="mastermind_space"
                                                entityId={spaceId}
                                                onTagsChange={() => {
                                                    queryClient.invalidateQueries({queryKey: ["/api/entities", "mastermind_space", spaceId, "tags"]});
                                                }}
                                                className="mt-2"
                                            />
                                        )}
                                    </CollapsibleContent>
                                </Collapsible>

                                {/* Linked Spaces */}
                                <Collapsible open={linkedSpacesOpen}
                                             onOpenChange={(v) => toggleSection("linkedSpaces", v, setLinkedSpacesOpen)}>
                                    <div className="flex items-center gap-1.5">
                                        <CollapsibleTrigger
                                            className="flex items-center gap-1.5 flex-1 text-xs font-medium text-muted-foreground hover:text-foreground group">
                                            <ChevronDown className="w-3.5 h-3.5 group-data-[state=closed]:hidden"/>
                                            <ChevronRight className="w-3.5 h-3.5 group-data-[state=open]:hidden"/>
                                            <Network className="w-3.5 h-3.5"/>
                                            <span>Linked Spaces</span>
                                            <Badge variant="secondary"
                                                   className="ml-auto text-[10px] px-1.5 h-4">{relationships.length}</Badge>
                                        </CollapsibleTrigger>
                                        {availableSpacesToLink.length > 0 && (
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button variant="ghost" size="icon" className="h-5 w-5"
                                                            data-testid="button-link-space">
                                                        <Plus className="w-3 h-3"/>
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end" className="max-h-48 overflow-y-auto">
                                                    {availableSpacesToLink.map((s) => (
                                                        <DropdownMenuItem
                                                            key={s.id}
                                                            onClick={() => createRelationshipMutation.mutate(s.id)}
                                                            data-testid={`menu-link-space-${s.id}`}
                                                        >
                                                            <Link2 className="w-3 h-3 mr-2"/>
                                                            {s.name}
                                                        </DropdownMenuItem>
                                                    ))}
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        )}
                                    </div>
                                    <CollapsibleContent className="mt-2 space-y-0.5 pl-5">
                                        {relationships.length > 0 ? (
                                            relationships.map((rel) => {
                                                const linkedId = rel.targetSpaceId === spaceId ? rel.sourceSpaceId : rel.targetSpaceId;
                                                return (
                                                    <div
                                                        key={rel.id}
                                                        className="flex items-center gap-2 py-1.5 px-2 rounded-md text-xs group"
                                                    >
                                                        <Link2 className="w-3 h-3 text-muted-foreground"/>
                                                        <span
                                                            className="truncate flex-1 cursor-pointer hover:text-primary"
                                                            onClick={() => setLocation(`/spaces/${linkedId}`)}
                                                        >
                              {linkedSpaces[linkedId]?.name || "Space"}
                            </span>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity"
                                                            onClick={() => deleteRelationshipMutation.mutate(rel.id)}
                                                            data-testid={`button-unlink-space-${rel.id}`}
                                                        >
                                                            <X className="w-2.5 h-2.5"/>
                                                        </Button>
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <p className="text-xs text-muted-foreground py-1.5 px-2">No linked
                                                spaces</p>
                                        )}
                                    </CollapsibleContent>
                                </Collapsible>

                                {/* Members */}
                                {space && (
                                    <Collapsible open={membersOpen}
                                                 onOpenChange={(v) => toggleSection("members", v, setMembersOpen)}>
                                        <CollapsibleTrigger
                                            className="flex items-center gap-1.5 w-full text-xs font-medium text-muted-foreground hover:text-foreground group">
                                            <ChevronDown className="w-3.5 h-3.5 group-data-[state=closed]:hidden"/>
                                            <ChevronRight className="w-3.5 h-3.5 group-data-[state=open]:hidden"/>
                                            <Users className="w-3.5 h-3.5"/>
                                            <span>Members</span>
                                            <Badge variant="secondary"
                                                   className="ml-auto text-[10px] px-1.5 h-4">{spaceMembers.length}</Badge>
                                        </CollapsibleTrigger>
                                        <CollapsibleContent className="mt-2 space-y-0.5 pl-5">
                                            {isLoadingMembers ? (
                                                <p className="text-xs text-muted-foreground py-1.5 px-2">Loading...</p>
                                            ) : spaceMembers.length > 0 ? (
                                                spaceMembers.map((member) => {
                                                    const memberUser = memberUsers[member.userId];
                                                    const isCurrentUser = user?.id === member.userId;
                                                    const isWorkspaceMirror = member.mirroredFromWorkspace === true;
                                                    const canManageSingular =
                                                        isOwner &&
                                                        member.spaceRole !== "owner" &&
                                                        !isWorkspaceMirror &&
                                                        !!member.accessId;
                                                    return (
                                                        <div
                                                            key={`${member.userId}-${member.accessId ?? "owner"}`}
                                                            className="flex flex-wrap items-center gap-2 py-1.5 px-2 rounded-md text-xs group"
                                                        >
                                                            <User className="w-3 h-3 text-muted-foreground"/>
                                                            <span className="truncate flex-1 min-w-0">
                                                                {isCurrentUser ? "You" : (memberUser?.name || memberUser?.username || memberUser?.email || "Anonymous User")}
                                                            </span>
                                                            {member.spaceRole === "owner" && (
                                                                <Badge variant="outline"
                                                                       className="text-[10px] px-1.5 py-0 h-4 text-primary border-primary/30">
                                                                    Owner
                                                                </Badge>
                                                            )}
                                                            {isOwner && member.spaceRole !== "owner" && isWorkspaceMirror && (
                                                                <>
                                                                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 capitalize">
                                                                        {member.spaceRole}
                                                                    </Badge>
                                                                    <span className="text-[10px] text-muted-foreground w-full pl-5 basis-full">
                                                                        Workspace member — manage in Workspace settings
                                                                    </span>
                                                                </>
                                                            )}
                                                            {canManageSingular && (
                                                                <Select
                                                                    value={member.spaceRole}
                                                                    onValueChange={(v) =>
                                                                        updateMemberRoleMutation.mutate({
                                                                            memberUserId: member.userId,
                                                                            role: v as "editor" | "viewer",
                                                                        })
                                                                    }
                                                                    disabled={updateMemberRoleMutation.isPending}
                                                                >
                                                                    <SelectTrigger className="h-6 w-[92px] text-[10px] px-1.5">
                                                                        <SelectValue />
                                                                    </SelectTrigger>
                                                                    <SelectContent>
                                                                        <SelectItem value="editor">Editor</SelectItem>
                                                                        <SelectItem value="viewer">Viewer</SelectItem>
                                                                    </SelectContent>
                                                                </Select>
                                                            )}
                                                            {!isOwner && member.spaceRole === "editor" && (
                                                                <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">Editor</Badge>
                                                            )}
                                                            {!isOwner && member.spaceRole === "viewer" && (
                                                                <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4">Viewer</Badge>
                                                            )}
                                                            {canManageSingular && (
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity"
                                                                    onClick={() => {
                                                                        if (confirm("Are you sure you want to remove this member?")) {
                                                                            removeMemberMutation.mutate(member.accessId!);
                                                                        }
                                                                    }}
                                                                    disabled={removeMemberMutation.isPending}
                                                                    data-testid={`button-remove-member-${member.userId}`}
                                                                >
                                                                    <X className="w-2.5 h-2.5"/>
                                                                </Button>
                                                            )}
                                                        </div>
                                                    );
                                                })
                                            ) : (
                                                <p className="text-xs text-muted-foreground py-1.5 px-2">No members</p>
                                            )}
                                        </CollapsibleContent>
                                    </Collapsible>
                                )}
                            </div>
                        </ScrollArea>
                        )}
                    </div>

                    {/* Center Column - Main Content (ref for ResizeObserver - Topics tab uses measured width) */}
                    <div ref={centerColumnRef} className="flex-1 flex flex-col min-h-0 min-w-0 overflow-hidden w-full">
                        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0 min-w-0">
                            <div className="border-b px-4 shrink-0">
                                <TabsList className="h-10 bg-transparent">
                                    <TabsTrigger value="topics" className="gap-1.5 text-sm" data-testid="tab-topics">
                                        <FolderOpen className="w-4 h-4"/>
                                        Topics
                                    </TabsTrigger>
                                    <TabsTrigger value="threads" className="gap-1.5 text-sm" data-testid="tab-threads">
                                        <MessageSquare className="w-4 h-4"/>
                                        Threads
                                    </TabsTrigger>
                                    <TabsTrigger value="documents" className="gap-1.5 text-sm"
                                                 data-testid="tab-documents">
                                        <FileText className="w-4 h-4"/>
                                        Docs
                                    </TabsTrigger>
                                    <TabsTrigger value="notes" className="gap-1.5 text-sm" data-testid="tab-notes">
                                        <StickyNote className="w-4 h-4"/>
                                        Space Notes
                                    </TabsTrigger>
                                    <TabsTrigger value="context-lense" className="gap-1.5 text-sm"
                                                 data-testid="tab-context-lense">
                                        <Eye className="w-4 h-4"/>
                                        AI Context Lense
                                    </TabsTrigger>
                                </TabsList>
                            </div>

                            <ScrollArea className="flex-1 min-h-0 min-w-0 overflow-x-hidden">
                                {/* Topics Tab - same layout as Threads (do not change Threads tab) */}
                                <TabsContent value="topics" className="px-4 pb-4 pt-2 m-0 min-w-0 flex flex-col overflow-hidden overflow-x-hidden w-full data-[state=active]:flex-1">
                                    <div className="space-y-4 min-w-0 flex-1 flex flex-col overflow-hidden overflow-x-hidden min-h-0 w-full max-w-full" style={{ minWidth: 0 }}>
                                        <SectionErrorBoundary sectionName="Topics management">
                                            <TopicsManagement
                                                spaceId={spaceId}
                                                topics={topics}
                                                spaceDataBeingLoaded={spaceDataBeingLoaded}
                                                allThreads={allThreads}
                                                allSpaces={allSpaces}
                                                allDocuments={allDocuments}
                                                isSharedSpace={!!(space as { isShared?: boolean } | null)?.isShared}
                                                refreshKnowledge={refreshKnowledge}
                                                contentWidthPx={centerContentWidthPx}
                                                initialExpandedTopicId={expandTopicIdFromUrl}
                                                selectedTopicId={selectedTopicIdForLink}
                                                onTopicSelect={(id) => setSelectedTopicIdForLink(id ?? null)}
                                                spaceMemberRole={mySpaceRole}
                                                spaceOwnerUserId={spaceOwnerUserId}
                                                itemOverrides={myItemOverrides}
                                            />
                                        </SectionErrorBoundary>
                                    </div>
                                </TabsContent>

                                {/* Notes Tab */}
                                <TabsContent value="notes" className="p-4 m-0">
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="flex items-center gap-2">
                                                <StickyNote className="w-5 h-5 text-muted-foreground"/>
                                                <h3 className="font-semibold">Space Notes</h3>
                                            </div>
                                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                {!notesColumnExists ? (
                                                    <span className="flex items-center gap-1 text-amber-500">
                                                        <span>Not saving (setup required)</span>
                                                    </span>
                                                ) : isNotesSaving ? (
                                                    <span className="flex items-center gap-1">
                                                        <span className="animate-pulse">Saving...</span>
                                                    </span>
                                                ) : (
                                                    <span className="flex items-center gap-1">
                                                        <CheckCircle2 className="w-3 h-3 text-green-500"/>
                                                        <span>Saved</span>
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        {!notesColumnExists && (
                                            <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-md text-sm">
                                                <p className="font-medium text-amber-600 dark:text-amber-400">Database Setup Required</p>
                                                <p className="text-muted-foreground mt-1">
                                                    To enable notes, add a <code className="bg-muted px-1 rounded">notes</code> TEXT column to your <code className="bg-muted px-1 rounded">mastermind</code> table in Supabase.
                                                </p>
                                            </div>
                                        )}
                                        <p className="text-sm text-muted-foreground">
                                            Keep your thoughts, ideas, and notes about this space. These notes are always available and saved automatically.
                                        </p>
                                        <div className="border rounded-md">
                                            <RichTextEditor
                                                content={notesContent}
                                                onChange={handleNotesChange}
                                                placeholder="Start writing your notes here..."
                                                data-testid="editor-space-notes"
                                            />
                                        </div>
                                    </div>
                                </TabsContent>

                                {/* Documents Tab */}
                                <TabsContent value="documents" className="p-4 m-0 h-full">
                                    <SectionErrorBoundary sectionName="Document grid">
                                        <DocumentGrid
                                            spaceId={spaceId}
                                            spaceNotes={notesContent}
                                            spaceName={space?.name}
                                            topics={topics}
                                            space={space}
                                            allSpaces={allSpaces}
                                            allDocuments={allDocuments}
                                            refreshKnowledge={refreshKnowledge}
                                            isLoading={isLoadingDocuments}
                                            spaceMemberRole={mySpaceRole}
                                            spaceOwnerUserId={spaceOwnerUserId}
                                            itemOverrides={myItemOverrides}
                                        />
                                    </SectionErrorBoundary>
                                </TabsContent>

                                {/* Sources Tab */}
                                <TabsContent value="sources" className="p-4 m-0">
                                    <div className="space-y-6">
                                        {/* Reference Sources Section - Compact Spreadsheet Style */}
                                        <div>
                                            <div className="flex items-center justify-between gap-2 mb-2">
                                                <h3 className="font-semibold text-sm">Reference Sources</h3>
                                                <div className="flex items-center gap-1">
                                                    {availableSourcesToLink.length > 0 && (
                                                        <DropdownMenu>
                                                            <DropdownMenuTrigger asChild>
                                                                <Button size="sm" variant="ghost" className="h-7 px-2"
                                                                        data-testid="button-link-existing-source">
                                                                    <Link2 className="w-3.5 h-3.5"/>
                                                                </Button>
                                                            </DropdownMenuTrigger>
                                                            <DropdownMenuContent align="end"
                                                                                 className="max-h-64 overflow-y-auto w-64">
                                                                {availableSourcesToLink.map((source) => (
                                                                    <DropdownMenuItem
                                                                        key={source.id}
                                                                        onClick={() => linkSourceMutation.mutate(source.id)}
                                                                        disabled={isSourceMutationPending}
                                                                        data-testid={`menu-link-source-${source.id}`}
                                                                        className="flex items-center gap-2"
                                                                    >
                                                                        {source.googleDriveUrl ? (
                                                                            <FileIcon
                                                                                className="w-3.5 h-3.5 text-blue-500 flex-shrink-0"/>
                                                                        ) : source.url ? (
                                                                            <Globe
                                                                                className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0"/>
                                                                        ) : (
                                                                            <BookOpen
                                                                                className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0"/>
                                                                        )}
                                                                        <span
                                                                            className="truncate text-xs">{source.title || "Untitled Source"}</span>
                                                                    </DropdownMenuItem>
                                                                ))}
                                                            </DropdownMenuContent>
                                                        </DropdownMenu>
                                                    )}
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        className="h-7 px-2"
                                                        onClick={() => setIsAddSourceDialogOpen(true)}
                                                        data-testid="button-add-source"
                                                    >
                                                        <Plus className="w-3.5 h-3.5"/>
                                                    </Button>
                                                </div>
                                            </div>
                                            {nativeLinkedSources.length > 0 || mergedLinkedSources.length > 0 ? (
                                                <>
                                                    {nativeLinkedSources.length > 0 ? (
                                                        <div className="border rounded-md divide-y">
                                                            {nativeLinkedSources.map((source) => {
                                                                const sourceUrl = source.googleDriveUrl || source.url;
                                                                return (
                                                                    <div
                                                                        key={source.id}
                                                                        className="flex items-center gap-2 px-2 py-1.5 hover-elevate group text-xs"
                                                                        data-testid={`row-source-${source.id}`}
                                                                    >
                                                                        {source.googleDriveUrl ? (
                                                                            <FileIcon
                                                                                className="w-3.5 h-3.5 text-blue-500 flex-shrink-0"/>
                                                                        ) : source.url ? (
                                                                            <Globe
                                                                                className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0"/>
                                                                        ) : (
                                                                            <BookOpen
                                                                                className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0"/>
                                                                        )}
                                                                        <div className="flex-1 min-w-0 flex items-center gap-2">
                                                                            {sourceUrl ? (
                                                                                <a
                                                                                    href={sourceUrl}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    className="truncate hover:underline hover:text-primary"
                                                                                    data-testid={`link-source-tab-${source.id}`}
                                                                                >
                                                                                    {source.title || "Untitled Source"}
                                                                                </a>
                                                                            ) : (
                                                                                <span
                                                                                    className="truncate">{source.title || "Untitled Source"}</span>
                                                                            )}
                                                                            {sourceUrl && (
                                                                                <ExternalLink
                                                                                    className="w-3 h-3 text-muted-foreground flex-shrink-0 opacity-0 group-hover:opacity-100"/>
                                                                            )}
                                                                        </div>
                                                                        <Badge variant="secondary"
                                                                               className="text-[10px] px-1.5 py-0 h-4 flex-shrink-0">
                                                                            {source.type || "text"}
                                                                        </Badge>
                                                                        {source.googleDriveUrl && (
                                                                            <Badge variant="outline"
                                                                                   className="text-[10px] px-1.5 py-0 h-4 text-blue-600 flex-shrink-0">
                                                                                Drive
                                                                            </Badge>
                                                                        )}
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="icon"
                                                                            className="h-5 w-5 opacity-0 group-hover:opacity-100 flex-shrink-0"
                                                                            onClick={() => unlinkSourceMutation.mutate(source.id)}
                                                                            disabled={isSourceMutationPending}
                                                                            data-testid={`button-unlink-source-tab-${source.id}`}
                                                                        >
                                                                            <X className="w-3 h-3"/>
                                                                        </Button>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    ) : null}
                                                    <SpaceMergedSection title="Merged sources" className="min-w-0" itemCount={mergedLinkedSources.length}>
                                                        <div className="border rounded-md divide-y">
                                                            {mergedLinkedSources.map((source) => {
                                                                const sourceUrl = source.googleDriveUrl || source.url;
                                                                return (
                                                                    <MergedRowChrome key={source.id} merged showBadge>
                                                                        <div
                                                                            className="flex items-center gap-2 px-2 py-1.5 hover-elevate group text-xs"
                                                                            data-testid={`row-source-${source.id}`}
                                                                        >
                                                                            {source.googleDriveUrl ? (
                                                                                <FileIcon
                                                                                    className="w-3.5 h-3.5 text-blue-500 flex-shrink-0"/>
                                                                            ) : source.url ? (
                                                                                <Globe
                                                                                    className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0"/>
                                                                            ) : (
                                                                                <BookOpen
                                                                                    className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0"/>
                                                                            )}
                                                                            <div className="flex-1 min-w-0 flex items-center gap-2">
                                                                                {sourceUrl ? (
                                                                                    <a
                                                                                        href={sourceUrl}
                                                                                        target="_blank"
                                                                                        rel="noopener noreferrer"
                                                                                        className="truncate hover:underline hover:text-primary"
                                                                                        data-testid={`link-source-tab-${source.id}`}
                                                                                    >
                                                                                        {source.title || "Untitled Source"}
                                                                                    </a>
                                                                                ) : (
                                                                                    <span
                                                                                        className="truncate">{source.title || "Untitled Source"}</span>
                                                                                )}
                                                                                {sourceUrl && (
                                                                                    <ExternalLink
                                                                                        className="w-3 h-3 text-muted-foreground flex-shrink-0 opacity-0 group-hover:opacity-100"/>
                                                                                )}
                                                                            </div>
                                                                            <Badge variant="secondary"
                                                                                   className="text-[10px] px-1.5 py-0 h-4 flex-shrink-0">
                                                                                {source.type || "text"}
                                                                            </Badge>
                                                                            {source.googleDriveUrl && (
                                                                                <Badge variant="outline"
                                                                                       className="text-[10px] px-1.5 py-0 h-4 text-blue-600 flex-shrink-0">
                                                                                    Drive
                                                                                </Badge>
                                                                            )}
                                                                            <Button
                                                                                variant="ghost"
                                                                                size="icon"
                                                                                className="h-5 w-5 opacity-0 group-hover:opacity-100 flex-shrink-0"
                                                                                onClick={() => unlinkSourceMutation.mutate(source.id)}
                                                                                disabled={isSourceMutationPending}
                                                                                data-testid={`button-unlink-source-tab-${source.id}`}
                                                                            >
                                                                                <X className="w-3 h-3"/>
                                                                            </Button>
                                                                        </div>
                                                                    </MergedRowChrome>
                                                                );
                                                            })}
                                                        </div>
                                                    </SpaceMergedSection>
                                                </>
                                            ) : (
                                                <div
                                                    className="border rounded-md p-4 text-center text-xs text-muted-foreground">
                                                    <BookOpen className="w-6 h-6 mx-auto mb-2 opacity-50"/>
                                                    <p>No sources linked yet</p>
                                                    <div className="flex items-center justify-center gap-2 mt-2">
                                                        {availableSourcesToLink.length > 0 && (
                                                            <DropdownMenu>
                                                                <DropdownMenuTrigger asChild>
                                                                    <Button size="sm" variant="outline"
                                                                            className="h-7 text-xs"
                                                                            data-testid="button-link-first-existing">
                                                                        <Link2 className="w-3 h-3 mr-1"/>
                                                                        Link
                                                                    </Button>
                                                                </DropdownMenuTrigger>
                                                                <DropdownMenuContent align="center"
                                                                                     className="max-h-64 overflow-y-auto w-64">
                                                                    {availableSourcesToLink.map((source) => (
                                                                        <DropdownMenuItem
                                                                            key={source.id}
                                                                            onClick={() => linkSourceMutation.mutate(source.id)}
                                                                            disabled={isSourceMutationPending}
                                                                        >
                                                                            <BookOpen className="w-3.5 h-3.5 mr-2"/>
                                                                            <span
                                                                                className="truncate text-xs">{source.title || "Untitled"}</span>
                                                                        </DropdownMenuItem>
                                                                    ))}
                                                                </DropdownMenuContent>
                                                            </DropdownMenu>
                                                        )}
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            className="h-7 text-xs"
                                                            onClick={() => setIsAddSourceDialogOpen(true)}
                                                            data-testid="button-create-first-source"
                                                        >
                                                            <Plus className="w-3 h-3 mr-1"/>
                                                            New
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Subject Matter Experts Section */}
                                        <div>
                                            <div className="flex items-center justify-between gap-2 mb-2">
                                                <h3 className="font-semibold text-sm">Subject Matter Experts</h3>
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    className="h-7 px-2"
                                                    onClick={() => setIsAddExpertDialogOpen(true)}
                                                    data-testid="button-add-expert"
                                                >
                                                    <Plus className="w-3.5 h-3.5"/>
                                                </Button>
                                            </div>
                                            {((space?.subjectMatterExperts as SubjectMatterExpert[]) || []).length > 0 ? (
                                                <div className="border rounded-md divide-y">
                                                    {((space?.subjectMatterExperts as SubjectMatterExpert[]) || []).map((expert) => (
                                                        <div
                                                            key={expert.id}
                                                            className="flex items-center gap-2 px-2 py-1.5 hover-elevate group text-xs"
                                                            data-testid={`row-expert-${expert.id}`}
                                                        >
                                                            <User
                                                                className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0"/>
                                                            <span className="font-medium truncate">{expert.name}</span>
                                                            <span
                                                                className="text-muted-foreground truncate flex-1">{expert.role}</span>
                                                            {expert.notes && (
                                                                <span
                                                                    className="text-muted-foreground truncate max-w-[150px] hidden sm:block">{expert.notes}</span>
                                                            )}
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-5 w-5 opacity-0 group-hover:opacity-100 flex-shrink-0"
                                                                onClick={() => {
                                                                    const experts = ((space?.subjectMatterExperts as SubjectMatterExpert[]) || []).filter(e => e.id !== expert.id);
                                                                    updateSpaceMutation.mutate({subjectMatterExperts: experts});
                                                                }}
                                                                data-testid={`button-remove-expert-${expert.id}`}
                                                            >
                                                                <X className="w-3 h-3"/>
                                                            </Button>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div
                                                    className="border rounded-md p-4 text-center text-xs text-muted-foreground">
                                                    <User className="w-6 h-6 mx-auto mb-2 opacity-50"/>
                                                    <p>No experts added yet</p>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        className="h-7 text-xs mt-2"
                                                        onClick={() => setIsAddExpertDialogOpen(true)}
                                                        data-testid="button-add-first-expert"
                                                    >
                                                        <Plus className="w-3 h-3 mr-1"/>
                                                        Add Expert
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </TabsContent>

                                {/* Threads Tab - full width of center column */}
                                <TabsContent value="threads" className="px-4 pb-4 pt-2 m-0 min-w-0 flex flex-col overflow-hidden w-full data-[state=active]:flex-1">
                                    <div className="space-y-3 min-w-0 flex-1 flex flex-col overflow-hidden min-h-0 w-full max-w-full">
                                        {/* DS EDIT 5aug26 — repacked: title + icon search/sort/views + link on ONE row (dead space killed) */}
                                        <div className="flex items-center gap-1.5 min-w-0 shrink-0">
                                            <h3 className="font-semibold whitespace-nowrap truncate min-w-0">Threads</h3>
                                            <div className="flex flex-1 items-center justify-end gap-1 min-w-0">
                                                {linkedThreads.length > 0 && (
                                                    <>
                                                        <IconSearch
                                                            value={threadSearchQuery}
                                                            onChange={setThreadSearchQuery}
                                                            placeholder="Search threads..."
                                                            testId="input-search-threads"
                                                        />
                                                        <IconSelectMenu
                                                            label="Sort threads"
                                                            value={`${threadSortField}-${threadSortDirection}`}
                                                            onValueChange={(value) => {
                                                                const [field, direction] = value.split("-") as [ThreadSortField, SortDirection];
                                                                setThreadSortField(field);
                                                                setThreadSortDirection(direction);
                                                            }}
                                                            testId="select-sort-threads"
                                                            options={[
                                                                {value: "lineage-asc", label: "Topic / Lineage", icon: ArrowUp, testId: "sort-threads-lineage-asc"},
                                                                {value: "lineage-desc", label: "Topic / Lineage (Z→A)", icon: ArrowDown, testId: "sort-threads-lineage-desc"},
                                                                {value: "title-asc", label: "A → Z", icon: ArrowUp, testId: "sort-threads-title-asc"},
                                                                {value: "title-desc", label: "Z → A", icon: ArrowDown, testId: "sort-threads-title-desc"},
                                                                {value: "createdAt-desc", label: "Newest", icon: ArrowDown, testId: "sort-threads-created-desc"},
                                                                {value: "createdAt-asc", label: "Oldest", icon: ArrowUp, testId: "sort-threads-created-asc"},
                                                                {value: "updatedAt-desc", label: "Recent", icon: ArrowDown, testId: "sort-threads-updated-desc"},
                                                                {value: "updatedAt-asc", label: "Least recent", icon: ArrowUp, testId: "sort-threads-updated-asc"},
                                                            ]}
                                                        />
                                                        <div className="flex items-center gap-0.5 border rounded-md p-0.5 shrink-0">
                                                            <Button
                                                                variant={threadViewMode === "list" ? "secondary" : "ghost"}
                                                                size="icon"
                                                                className="h-7 w-7"
                                                                onClick={() => setThreadViewMode("list")}
                                                                data-testid="button-view-threads-list"
                                                                title="List view"
                                                            >
                                                                <List className="h-3.5 w-3.5" />
                                                            </Button>
                                                            <Button
                                                                variant={threadViewMode === "grid" ? "secondary" : "ghost"}
                                                                size="icon"
                                                                className="h-7 w-7"
                                                                onClick={() => setThreadViewMode("grid")}
                                                                data-testid="button-view-threads-grid"
                                                                title="Column view"
                                                            >
                                                                <LayoutGrid className="h-3.5 w-3.5" />
                                                            </Button>
                                                        </div>
                                                    </>
                                                )}
                                                {availableThreadsToLink.length > 0 && (
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <Button size="sm" variant="ghost" className="h-9 w-9 px-0 shrink-0"
                                                                    title="Link existing thread"
                                                                    data-testid="button-link-existing-thread">
                                                                <Link2 className="w-3.5 h-3.5"/>
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end"
                                                                             className="max-h-64 overflow-y-auto w-64">
                                                            {availableThreadsToLink.map((thread) => (
                                                                <DropdownMenuItem
                                                                    key={thread.id}
                                                                    onClick={() => linkThreadMutation.mutate(thread.id)}
                                                                    disabled={isThreadMutationPending}
                                                                    data-testid={`menu-link-thread-${thread.id}`}
                                                                    className="flex items-center gap-2"
                                                                >
                                                                    <MessageSquare
                                                                        className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0"/>
                                                                    <span className="truncate text-xs block max-w-[180px]">
                                                                        {truncateTitle(thread.title || "Untitled Thread", THREAD_TITLE_MAX_LENGTH)}
                                                                    </span>
                                                                </DropdownMenuItem>
                                                            ))}
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                )}
                                            </div>
                                        </div>

                                        {/* Threads Content */}
                                        {isLoadingThreads ? (
                                            <div className="border rounded-md divide-y">
                                                {[1, 2, 3].map((i) => (
                                                    <div key={i} className="flex items-center gap-2 px-2 py-1.5 text-xs animate-pulse">
                                                        <div className="w-3.5 h-3.5 bg-muted rounded flex-shrink-0" />
                                                        <div className="flex-1 h-4 bg-muted rounded" />
                                                        <div className="w-12 h-4 bg-muted rounded flex-shrink-0" />
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (threadGroups.reduce((s, g) => s + g.threads.length, 0) === 0 &&
                                            mergedThreadsFilteredSorted.length === 0 &&
                                            threadSearchQuery) ? (
                                            <div className="border rounded-md p-6 text-center">
                                                <Search className="w-8 h-8 mx-auto mb-3 text-muted-foreground" />
                                                <p className="text-sm text-muted-foreground">
                                                    No threads match your search "{threadSearchQuery}"
                                                </p>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="mt-2"
                                                    onClick={() => setThreadSearchQuery("")}
                                                    data-testid="button-clear-thread-search"
                                                >
                                                    Clear search
                                                </Button>
                                            </div>
                                        ) : linkedThreads.length === 0 ? (
                                            <div className="border rounded-md p-4 text-center text-xs text-muted-foreground">
                                                <MessageSquare className="w-6 h-6 mx-auto mb-2 opacity-50"/>
                                                <p>No threads linked yet</p>
                                                <div className="flex items-center justify-center gap-2 mt-2">
                                                    {availableThreadsToLink.length > 0 && (
                                                        <DropdownMenu>
                                                            <DropdownMenuTrigger asChild>
                                                                <Button size="sm" variant="outline"
                                                                        className="h-7 text-xs"
                                                                        data-testid="button-link-first-thread">
                                                                    <Link2 className="w-3 h-3 mr-1"/>
                                                                    Link
                                                                </Button>
                                                            </DropdownMenuTrigger>
                                                            <DropdownMenuContent align="center"
                                                                                 className="max-h-64 overflow-y-auto w-64">
                                                                {availableThreadsToLink.map((thread) => (
                                                                    <DropdownMenuItem
                                                                        key={thread.id}
                                                                        onClick={() => linkThreadMutation.mutate(thread.id)}
                                                                        disabled={isThreadMutationPending}
                                                                    >
                                                                        <MessageSquare className="w-3.5 h-3.5 mr-2"/>
                                                                        <span className="truncate text-xs block max-w-[180px]">
                                                                            {truncateTitle(thread.title || "Untitled", THREAD_TITLE_MAX_LENGTH)}
                                                                        </span>
                                                                    </DropdownMenuItem>
                                                                ))}
                                                            </DropdownMenuContent>
                                                        </DropdownMenu>
                                                    )}
                                                </div>
                                            </div>
                                        ) : threadViewMode === "grid" ? (
                                            <div className="space-y-4">
                                                {threadGroups.map((group) => (
                                                    <div key={group.groupKey}>
                                                        <div className="flex items-center gap-2 mb-2 px-1">
                                                            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                                                                {group.groupKey === "UNLINKED" ? "Unlinked" : "Topic"}
                                                            </span>
                                                            <span className="text-sm font-medium" data-testid={`thread-group-${group.groupKey}`}>
                                                                {group.groupLabel}
                                                            </span>
                                                            <span className="text-xs text-muted-foreground">({group.threads.length})</span>
                                                        </div>
                                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                                            {group.threads.map((thread) => (
                                                                <ThreadItem
                                                                    key={thread.id}
                                                                    thread={thread}
                                                                    viewMode="grid"
                                                                    spaceId={spaceId}
                                                                    topicId={thread.topicId || undefined}
                                                                    topicNameMap={topicNameMap}
                                                                    embeddingStatusMap={threadEmbeddingStatusMap}
                                                                    lineageLabel={threadLineageMap.get(thread.id)?.lineageLabel}
                                                                    onRefresh={() => {
                                                                        queryClient.refetchQueries({queryKey: ["/api/chat-thread-summaries"], exact: false});
                                                                    }}
                                                                    spaceMemberRole={mySpaceRole}
                                                                    spaceOwnerUserId={spaceOwnerUserId}
                                                                    itemOverrides={myItemOverrides}
                                                                    isSharedSpace={!!(space as { isShared?: boolean } | null)?.isShared}
                                                                />
                                                            ))}
                                                        </div>
                                                    </div>
                                                ))}
                                                <SpaceMergedSection title="Merged threads" className="min-w-0" itemCount={mergedThreadsFilteredSorted.length}>
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                                        {mergedThreadsFilteredSorted.map((thread) => (
                                                            <MergedRowChrome key={thread.id} merged showBadge>
                                                                <ThreadItem
                                                                    thread={thread}
                                                                    viewMode="grid"
                                                                    spaceId={spaceId}
                                                                    topicId={thread.topicId || undefined}
                                                                    topicNameMap={topicNameMap}
                                                                    embeddingStatusMap={threadEmbeddingStatusMap}
                                                                    lineageLabel={threadLineageMap.get(thread.id)?.lineageLabel}
                                                                    onRefresh={() => {
                                                                        queryClient.refetchQueries({
                                                                            queryKey: ["/api/chat-thread-summaries"],
                                                                            exact: false,
                                                                        });
                                                                    }}
                                                                    spaceMemberRole={mySpaceRole}
                                                                    spaceOwnerUserId={spaceOwnerUserId}
                                                                    itemOverrides={myItemOverrides}
                                                                    isSharedSpace={
                                                                        !!(space as { isShared?: boolean } | null)?.isShared
                                                                    }
                                                                />
                                                            </MergedRowChrome>
                                                        ))}
                                                    </div>
                                                </SpaceMergedSection>
                                            </div>
                                        ) : (
                                            <div className="w-full min-w-0 max-w-full pb-4 overflow-hidden flex-1">
                                                <div className="border rounded-md min-w-0 w-full overflow-hidden">
                                                    {/* List header row - Thread, Lineage, Updated */}
                                                    <div className="grid grid-cols-[auto_1fr_auto_auto_auto] gap-3 items-center px-3 py-2 border-b bg-muted/50 text-xs font-medium text-muted-foreground">
                                                        <div className="w-8" />
                                                        <div>Thread</div>
                                                        <div className="max-w-[180px]">Lineage</div>
                                                        <div className="hidden xl:block w-20 text-right">Updated</div>
                                                        <div className="w-8" />
                                                    </div>
                                                    {threadGroups.map((group) => (
                                                        <div key={group.groupKey}>
                                                            <div className="flex items-center gap-2 px-3 py-2 bg-muted/30 border-b text-sm font-medium">
                                                                <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                                                                    {group.groupKey === "UNLINKED" ? "Unlinked" : "Topic"}
                                                                </span>
                                                                <span data-testid={`thread-group-${group.groupKey}`}>{group.groupLabel}</span>
                                                                <span className="text-xs text-muted-foreground">({group.threads.length})</span>
                                                            </div>
                                                            <div className="divide-y">
                                                                {group.threads.map((thread) => (
                                                                    <ThreadItem
                                                                        key={thread.id}
                                                                        thread={thread}
                                                                        viewMode="list"
                                                                        spaceId={spaceId}
                                                                        topicId={thread.topicId || undefined}
                                                                        topicNameMap={topicNameMap}
                                                                        embeddingStatusMap={threadEmbeddingStatusMap}
                                                                        lineageLabel={threadLineageMap.get(thread.id)?.lineageLabel}
                                                                        onRefresh={() => {
                                                                            queryClient.refetchQueries({queryKey: ["/api/chat-thread-summaries"], exact: false});
                                                                        }}
                                                                        spaceMemberRole={mySpaceRole}
                                                                        spaceOwnerUserId={spaceOwnerUserId}
                                                                        itemOverrides={myItemOverrides}
                                                                        isSharedSpace={!!(space as { isShared?: boolean } | null)?.isShared}
                                                                    />
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                                <SpaceMergedSection title="Merged threads" className="mt-3 min-w-0" itemCount={mergedThreadsFilteredSorted.length}>
                                                    <div className="border rounded-md min-w-0 w-full overflow-hidden divide-y">
                                                        {mergedThreadsFilteredSorted.map((thread) => (
                                                            <MergedRowChrome key={thread.id} merged showBadge>
                                                                <ThreadItem
                                                                    thread={thread}
                                                                    viewMode="list"
                                                                    spaceId={spaceId}
                                                                    topicId={thread.topicId || undefined}
                                                                    topicNameMap={topicNameMap}
                                                                    embeddingStatusMap={threadEmbeddingStatusMap}
                                                                    lineageLabel={threadLineageMap.get(thread.id)?.lineageLabel}
                                                                    onRefresh={() => {
                                                                        queryClient.refetchQueries({
                                                                            queryKey: ["/api/chat-thread-summaries"],
                                                                            exact: false,
                                                                        });
                                                                    }}
                                                                    spaceMemberRole={mySpaceRole}
                                                                    spaceOwnerUserId={spaceOwnerUserId}
                                                                    itemOverrides={myItemOverrides}
                                                                    isSharedSpace={
                                                                        !!(space as { isShared?: boolean } | null)?.isShared
                                                                    }
                                                                />
                                                            </MergedRowChrome>
                                                        ))}
                                                    </div>
                                                </SpaceMergedSection>
                                            </div>
                                        )}
                                    </div>
                                </TabsContent>

                                {/* Context Lense Tab - Inline Editing */}
                                <TabsContent value="context-lense" className="p-4 m-0">
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between gap-2">
                                            <div>
                                                <h3 className="font-semibold">Context Lense</h3>
                                                <p className="text-sm text-muted-foreground">
                                                    Define context that applies to all AI conversations in this space
                                                </p>
                                            </div>
                                            {(editLens.purpose !== currentLens.purpose ||
                                                editLens.priorities !== currentLens.priorities ||
                                                editLens.details !== currentLens.details) && (
                                                <Button
                                                    size="sm"
                                                    onClick={() => {
                                                        updateSpaceMutation.mutate({lens: editLens}, {
                                                            onSuccess: () => {
                                                                toast({
                                                                    title: "Lens Saved",
                                                                    description: "Your context lense has been saved"
                                                                });
                                                            }
                                                        });
                                                    }}
                                                    disabled={updateSpaceMutation.isPending}
                                                    data-testid="button-save-context-lense"
                                                >
                                                    <Save className="w-4 h-4 mr-2"/>
                                                    {updateSpaceMutation.isPending ? "Saving..." : "Save Changes"}
                                                </Button>
                                            )}
                                        </div>

                                        <div className="grid gap-4">
                                            {/* Purpose Field */}
                                            <Card className="p-4">
                                                <div className="flex items-start gap-3">
                                                    <div className="p-2 rounded-md bg-primary/10 flex-shrink-0">
                                                        <Target className="w-5 h-5 text-primary"/>
                                                    </div>
                                                    <div className="flex-1 space-y-2">
                                                        <Label htmlFor="lens-purpose"
                                                               className="font-medium text-sm">Purpose</Label>
                                                        <Textarea
                                                            id="lens-purpose"
                                                            placeholder="What is the main purpose of this space? (e.g., 'Research and develop marketing strategies for Q1 2024')"
                                                            value={editLens.purpose || ""}
                                                            onChange={(e) => setEditLens({
                                                                ...editLens,
                                                                purpose: e.target.value
                                                            })}
                                                            className="min-h-[80px] resize-none"
                                                            data-testid="textarea-lens-purpose"
                                                        />
                                                    </div>
                                                </div>
                                            </Card>

                                            {/* Priorities Field */}
                                            <Card className="p-4">
                                                <div className="flex items-start gap-3">
                                                    <div className="p-2 rounded-md bg-orange-500/10 flex-shrink-0">
                                                        <Zap className="w-5 h-5 text-orange-500"/>
                                                    </div>
                                                    <div className="flex-1 space-y-2">
                                                        <Label htmlFor="lens-priorities"
                                                               className="font-medium text-sm">Priorities</Label>
                                                        <Textarea
                                                            id="lens-priorities"
                                                            placeholder="What should the AI prioritize? (e.g., 'Focus on data-driven insights, actionable recommendations')"
                                                            value={editLens.priorities || ""}
                                                            onChange={(e) => setEditLens({
                                                                ...editLens,
                                                                priorities: e.target.value
                                                            })}
                                                            className="min-h-[80px] resize-none"
                                                            data-testid="textarea-lens-priorities"
                                                        />
                                                    </div>
                                                </div>
                                            </Card>

                                            {/* Details Field */}
                                            <Card className="p-4">
                                                <div className="flex items-start gap-3">
                                                    <div className="p-2 rounded-md bg-blue-500/10 flex-shrink-0">
                                                        <FileText className="w-5 h-5 text-blue-500"/>
                                                    </div>
                                                    <div className="flex-1 space-y-2">
                                                        <Label htmlFor="lens-details"
                                                               className="font-medium text-sm">Details</Label>
                                                        <Textarea
                                                            id="lens-details"
                                                            placeholder="Any additional context or constraints? (e.g., 'Target audience is B2B SaaS companies, budget is $50k')"
                                                            value={editLens.details || ""}
                                                            onChange={(e) => setEditLens({
                                                                ...editLens,
                                                                details: e.target.value
                                                            })}
                                                            className="min-h-[80px] resize-none"
                                                            data-testid="textarea-lens-details"
                                                        />
                                                    </div>
                                                </div>
                                            </Card>
                                        </div>
                                    </div>
                                </TabsContent>
                            </ScrollArea>
                        </Tabs>
                    </div>

                </div>
            </div>

            {/* Edit Space Dialog */}
            <Dialog open={isEditingSpace} onOpenChange={setIsEditingSpace}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Edit Space</DialogTitle>
                        <DialogDescription>Update your space details</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="edit-name">Name</Label>
                            <Input
                                id="edit-name"
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                data-testid="input-edit-space-name"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="edit-description">Description</Label>
                            <Textarea
                                id="edit-description"
                                value={editDescription}
                                onChange={(e) => setEditDescription(e.target.value)}
                                data-testid="input-edit-space-description"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsEditingSpace(false)}>Cancel</Button>
                        <Button onClick={handleSaveEdit} disabled={updateSpaceMutation.isPending}>
                            {updateSpaceMutation.isPending ? "Saving..." : "Save"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Add Goal Dialog */}
            <Dialog open={isAddingGoal} onOpenChange={setIsAddingGoal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Add Goal</DialogTitle>
                        <DialogDescription>Add a new goal to this space</DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        <Input
                            placeholder="Enter your goal..."
                            value={newGoal}
                            onChange={(e) => setNewGoal(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && !updateSpaceMutation.isPending && handleAddGoal()}
                            data-testid="input-new-goal"
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsAddingGoal(false)}
                                disabled={updateSpaceMutation.isPending}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleAddGoal}
                            disabled={!newGoal.trim() || updateSpaceMutation.isPending}
                            data-testid="button-confirm-add-goal"
                        >
                            {updateSpaceMutation.isPending ? "Adding..." : "Add Goal"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit Lens Dialog */}
            <Dialog open={isEditingLens} onOpenChange={setIsEditingLens}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Focus className="w-5 h-5"/>
                            Edit Space Lens
                        </DialogTitle>
                        <DialogDescription>
                            Define the context and perspective that will be applied to ALL AI conversations in this
                            space.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="lens-purpose">General Purpose</Label>
                            <Textarea
                                id="lens-purpose"
                                placeholder="What is the main purpose of this space? (e.g., 'Track and optimize my health journey focusing on nutrition and exercise')"
                                value={editLens.purpose}
                                onChange={(e) => setEditLens({...editLens, purpose: e.target.value})}
                                rows={2}
                                data-testid="input-lens-purpose"
                            />
                            <p className="text-xs text-muted-foreground">The overarching goal or focus for all work in
                                this space.</p>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="lens-priorities">Priorities</Label>
                            <Textarea
                                id="lens-priorities"
                                placeholder="What matters most? (e.g., 'Prioritize evidence-based approaches, long-term sustainability over quick fixes')"
                                value={editLens.priorities}
                                onChange={(e) => setEditLens({...editLens, priorities: e.target.value})}
                                rows={2}
                                data-testid="input-lens-priorities"
                            />
                            <p className="text-xs text-muted-foreground">Key priorities the AI should emphasize in
                                responses.</p>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="lens-details">Important Details to Remember</Label>
                            <Textarea
                                id="lens-details"
                                placeholder="Context the AI should always keep in mind (e.g., 'I have a history of knee problems, allergic to dairy, prefer morning workouts')"
                                value={editLens.details}
                                onChange={(e) => setEditLens({...editLens, details: e.target.value})}
                                rows={3}
                                data-testid="input-lens-details"
                            />
                            <p className="text-xs text-muted-foreground">Personal context, constraints, or preferences
                                relevant to this space.</p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsEditingLens(false)}
                                disabled={updateSpaceMutation.isPending}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSaveLens}
                            disabled={updateSpaceMutation.isPending}
                            data-testid="button-save-lens"
                        >
                            <Save className="w-4 h-4 mr-2"/>
                            {updateSpaceMutation.isPending ? "Saving..." : "Save Lens"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Hero Image Upload Dialog */}
            <Dialog open={isUploadDialogOpen} onOpenChange={(open) => {
                if (!open) cleanupHeroImageDialog();
                setIsUploadDialogOpen(open);
            }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Upload Hero Image</DialogTitle>
                        <DialogDescription>Add a hero image to personalize your space</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="hero-image-file">Select Image</Label>
                            <Input
                                id="hero-image-file"
                                type="file"
                                accept="image/*"
                                ref={fileInputRef}
                                onChange={handleFileUpload}
                                data-testid="input-hero-image-file"
                            />
                        </div>
                        {previewUrl && (
                            <div className="space-y-2">
                                <Label>Preview</Label>
                                <div className="relative aspect-video w-full overflow-hidden rounded-lg border">
                                    <img src={previewUrl} alt="Preview" className="w-full h-full object-cover"/>
                                    {isUploading && (
                                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                            <p className="text-white text-sm">Uploading...</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={() => {
                                cleanupHeroImageDialog();
                                setIsUploadDialogOpen(false);
                            }}>
                                Cancel
                            </Button>
                            <Button
                                onClick={() => uploadedImageUrl && uploadHeroMutation.mutate(uploadedImageUrl)}
                                disabled={!uploadedImageUrl || isUploading || uploadHeroMutation.isPending}
                            >
                                {uploadHeroMutation.isPending ? "Saving..." : isUploading ? "Uploading..." : "Save"}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Add Source Dialog */}
            <Dialog
                open={isAddSourceDialogOpen}
                onOpenChange={(open) => {
                    if (!open && isGoogleDrivePickerOpen) return;
                    if (!open) resetSourceDialog();
                    setIsAddSourceDialogOpen(open);
                }}
            >
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle>Add New Source</DialogTitle>
                        <DialogDescription>
                            Create a new source and automatically link it to this space
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex-1 overflow-y-auto py-4 space-y-4">
                        <Tabs value={sourceType} onValueChange={(v) => setSourceType(v as any)} className="w-full">
                            <TabsList className="grid w-full grid-cols-4">
                                <TabsTrigger value="text">Text</TabsTrigger>
                                <TabsTrigger value="url">URL</TabsTrigger>
                                <TabsTrigger value="upload">Upload</TabsTrigger>
                                <TabsTrigger value="google-doc">Google Drive</TabsTrigger>
                            </TabsList>

                            <div className="mt-4 space-y-4">
                                <div>
                                    <Label htmlFor="source-title">Title</Label>
                                    <Input
                                        id="source-title"
                                        placeholder="Enter source title"
                                        value={newSourceTitle}
                                        onChange={(e) => setNewSourceTitle(e.target.value)}
                                        data-testid="input-source-title"
                                    />
                                </div>

                                {sourceType === "text" && (
                                    <div>
                                        <Label htmlFor="source-content">Content</Label>
                                        <Textarea
                                            id="source-content"
                                            placeholder="Paste your content here..."
                                            className="min-h-[150px]"
                                            value={newSourceContent}
                                            onChange={(e) => setNewSourceContent(e.target.value)}
                                            data-testid="textarea-source-content"
                                        />
                                    </div>
                                )}

                                {sourceType === "url" && (
                                    <div>
                                        <Label htmlFor="source-url">URL</Label>
                                        <Input
                                            id="source-url"
                                            type="url"
                                            placeholder="https://example.com/article"
                                            value={newSourceUrl}
                                            onChange={(e) => setNewSourceUrl(e.target.value)}
                                            data-testid="input-source-url"
                                        />
                                        <p className="text-xs text-muted-foreground mt-1">
                                            Enter a web page URL to extract content from
                                        </p>
                                    </div>
                                )}

                                {sourceType === "upload" && (
                                    <div className="space-y-4">
                                        <input
                                            ref={sourceUploadRef}
                                            type="file"
                                            multiple
                                            className="hidden"
                                            onChange={handleSourceFileUpload}
                                        />
                                        <div className="border-2 border-dashed rounded-lg p-8 text-center">
                                            <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground"/>
                                            <p className="text-sm text-muted-foreground mb-2">
                                                Drag and drop files here, or click to browse
                                            </p>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => sourceUploadRef.current?.click()}
                                            >
                                                Choose Files
                                            </Button>
                                        </div>
                                        {sourceUploadFiles.length > 0 && (
                                            <div className="space-y-2">
                                                <p className="text-sm font-medium">Selected files</p>
                                                <ul className="space-y-1">
                                                    {sourceUploadFiles.map((file) => (
                                                        <li key={file.id}
                                                            className="flex items-center justify-between text-sm">
                                                            <a
                                                                href={file.url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-primary hover:underline truncate flex-1 mr-2"
                                                            >
                                                                {file.filename}
                                                            </a>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => setSourceUploadFiles(prev => prev.filter(f => f.id !== file.id))}
                                                            >
                                                                <Trash2 className="h-3 w-3"/>
                                                            </Button>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {sourceType === "google-doc" && (
                                    <div className="space-y-4">
                                        <GoogleDrivePicker
                                            onFilesSelected={(files) => {
                                                setSourceUploadFiles(files);
                                                if (files.length > 0 && !newSourceTitle) {
                                                    setNewSourceTitle(files[0].filename);
                                                }
                                            }}
                                            onPickerStateChange={setIsGoogleDrivePickerOpen}
                                            multiple={true}
                                        />
                                        {sourceUploadFiles.length > 0 && (
                                            <div className="space-y-2">
                                                <p className="text-sm font-medium">Selected files</p>
                                                <ul className="space-y-1">
                                                    {sourceUploadFiles.map((file) => (
                                                        <li key={file.id}
                                                            className="flex items-center justify-between text-sm">
                                                            <span
                                                                className="truncate flex-1 mr-2">{file.filename}</span>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => setSourceUploadFiles(prev => prev.filter(f => f.id !== file.id))}
                                                            >
                                                                <Trash2 className="h-3 w-3"/>
                                                            </Button>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Tags Selection */}
                                <div>
                                    <Label>Tags (optional)</Label>
                                    <Select
                                        value=""
                                        onValueChange={(tagId) => {
                                            if (!newSourceTags.includes(tagId)) {
                                                setNewSourceTags([...newSourceTags, tagId]);
                                            }
                                        }}
                                    >
                                        <SelectTrigger data-testid="select-source-tags">
                                            <SelectValue placeholder="Select tags..."/>
                                        </SelectTrigger>
                                        <SelectContent>
                                            {allTags.filter(t => !newSourceTags.includes(t.id)).map((tag) => (
                                                <SelectItem key={tag.id} value={tag.id}>{tag.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    {newSourceTags.length > 0 && (
                                        <div className="flex flex-wrap gap-1 mt-2">
                                            {newSourceTags.map((tagId) => {
                                                const tag = allTags.find(t => t.id === tagId);
                                                return (
                                                    <Badge key={tagId} variant="secondary" className="gap-1">
                                                        {tag?.name || tagId}
                                                        <button
                                                            type="button"
                                                            className="ml-1 hover:text-destructive"
                                                            onClick={() => setNewSourceTags(prev => prev.filter(id => id !== tagId))}
                                                        >
                                                            <X className="h-3 w-3"/>
                                                        </button>
                                                    </Badge>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                {/* Topics Selection */}
                                <div>
                                    <Label>Topics (optional)</Label>
                                    <Select
                                        value=""
                                        onValueChange={(topicId) => {
                                            if (!newSourceTopics.includes(topicId)) {
                                                setNewSourceTopics([...newSourceTopics, topicId]);
                                            }
                                        }}
                                    >
                                        <SelectTrigger data-testid="select-source-topics">
                                            <SelectValue placeholder="Select topics..."/>
                                        </SelectTrigger>
                                        <SelectContent>
                                            {topics.filter(t => !newSourceTopics.includes(t.id)).map((topic) => (
                                                <SelectItem key={topic.id} value={topic.id}>{topic.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    {newSourceTopics.length > 0 && (
                                        <div className="flex flex-wrap gap-1 mt-2">
                                            {newSourceTopics.map((topicId) => {
                                                const topic = topics.find(t => t.id === topicId);
                                                return (
                                                    <Badge key={topicId} variant="outline" className="gap-1">
                                                        {topic?.name || topicId}
                                                        <button
                                                            type="button"
                                                            className="ml-1 hover:text-destructive"
                                                            onClick={() => setNewSourceTopics(prev => prev.filter(id => id !== topicId))}
                                                        >
                                                            <X className="h-3 w-3"/>
                                                        </button>
                                                    </Badge>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Tabs>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                resetSourceDialog();
                                setIsAddSourceDialogOpen(false);
                            }}
                            disabled={createSourceMutation.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleAddSource}
                            disabled={createSourceMutation.isPending || !newSourceTitle.trim()}
                            data-testid="button-create-source"
                        >
                            {createSourceMutation.isPending ? "Creating..." : "Create & Link"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Add Subject Matter Expert Dialog */}
            <Dialog open={isAddExpertDialogOpen} onOpenChange={setIsAddExpertDialogOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Add Subject Matter Expert</DialogTitle>
                        <DialogDescription>
                            Add a person who has expertise relevant to this space
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="expert-name">Name</Label>
                            <Input
                                id="expert-name"
                                value={newExpertName}
                                onChange={(e) => setNewExpertName(e.target.value)}
                                placeholder="e.g., Dr. Jane Smith"
                                data-testid="input-expert-name"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="expert-role">Role / Expertise</Label>
                            <Input
                                id="expert-role"
                                value={newExpertRole}
                                onChange={(e) => setNewExpertRole(e.target.value)}
                                placeholder="e.g., Machine Learning Researcher"
                                data-testid="input-expert-role"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="expert-notes">Notes (optional)</Label>
                            <Textarea
                                id="expert-notes"
                                value={newExpertNotes}
                                onChange={(e) => setNewExpertNotes(e.target.value)}
                                placeholder="e.g., Contact via LinkedIn, author of XYZ paper"
                                className="h-20"
                                data-testid="input-expert-notes"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setNewExpertName("");
                                setNewExpertRole("");
                                setNewExpertNotes("");
                                setIsAddExpertDialogOpen(false);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={() => {
                                if (!newExpertName.trim() || !newExpertRole.trim()) {
                                    toast({
                                        title: "Required Fields",
                                        description: "Please enter name and role",
                                        variant: "destructive"
                                    });
                                    return;
                                }
                                const newExpert: SubjectMatterExpert = {
                                    id: crypto.randomUUID(),
                                    name: newExpertName.trim(),
                                    role: newExpertRole.trim(),
                                    notes: newExpertNotes.trim() || undefined,
                                };
                                const currentExperts = (space?.subjectMatterExperts as SubjectMatterExpert[]) || [];
                                updateSpaceMutation.mutate({subjectMatterExperts: [...currentExperts, newExpert]}, {
                                    onSuccess: () => {
                                        toast({
                                            title: "Expert Added",
                                            description: `${newExpertName} has been added to this space`
                                        });
                                        setNewExpertName("");
                                        setNewExpertRole("");
                                        setNewExpertNotes("");
                                        setIsAddExpertDialogOpen(false);
                                    }
                                });
                            }}
                            disabled={updateSpaceMutation.isPending || !newExpertName.trim() || !newExpertRole.trim()}
                            data-testid="button-save-expert"
                        >
                            {updateSpaceMutation.isPending ? "Adding..." : "Add Expert"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Rename Thread Dialog */}
            <Dialog open={isRenameThreadDialogOpen} onOpenChange={(open) => {
                setIsRenameThreadDialogOpen(open);
                if (!open) {
                    setThreadToRename(null);
                    setNewThreadTitle("");
                }
            }}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Rename Thread</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="thread-title">Thread Title</Label>
                            <Input
                                id="thread-title"
                                value={newThreadTitle}
                                onChange={(e) => setNewThreadTitle(e.target.value)}
                                placeholder="Enter thread title"
                                data-testid="input-thread-title"
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && newThreadTitle.trim() && threadToRename) {
                                        renameThreadMutation.mutate({
                                            threadId: threadToRename.id,
                                            title: newThreadTitle.trim()
                                        });
                                    }
                                }}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setIsRenameThreadDialogOpen(false);
                                setThreadToRename(null);
                                setNewThreadTitle("");
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={() => {
                                if (threadToRename && newThreadTitle.trim()) {
                                    renameThreadMutation.mutate({
                                        threadId: threadToRename.id,
                                        title: newThreadTitle.trim()
                                    });
                                }
                            }}
                            disabled={renameThreadMutation.isPending || !newThreadTitle.trim()}
                            data-testid="button-save-thread-title"
                        >
                            {renameThreadMutation.isPending ? "Saving..." : "Save"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Share Space Dialog */}
            {space && (
                <ShareSpaceDialog
                    open={isShareDialogOpen}
                    onOpenChange={setIsShareDialogOpen}
                    spaceId={space.id}
                    spaceName={space.name}
                />
            )}

            {/* Thread Linking Dialog */}
            {threadToLink && (
                <ThreadLinkingDialog
                    open={!!threadToLink}
                    onOpenChange={(open) => !open && setThreadToLink(null)}
                    linkedSpaceIds={threadToLink.linkedSpaceIds || []}
                    topicId={threadToLink.topicId || null}
                    tags={threadToLink.tags || []}
                    onUpdateSpaces={(spaceIds) => {
                        updateThreadSpacesMutation.mutate({threadId: threadToLink.id, spaceIds});
                    }}
                    onUpdateTopic={(topicId) => {
                        updateThreadTopicMutation.mutate({threadId: threadToLink.id, topicId});
                    }}
                    onUpdateTags={(tags) => {
                        updateThreadTagsMutation.mutate({threadId: threadToLink.id, tags});
                    }}
                />
            )}
        </div>
    );
}
